import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAdminTranslation } from '../../utils/useAdminTranslation';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Download,
  ChevronLeft,
  ChevronRight,
  Users,
  ShoppingCart,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  BarChart3,
  Check,
  ChevronsUpDown,
  Upload,
  FileText,
  X,
  Receipt,
  Send,
  Image as ImageIcon,
  ExternalLink,
  CheckCircle,
  Link as LinkIcon,
  FileCheck,
  MessageSquare,
  Mail,
  Copy,
  Globe,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { IncomeInvoicesTab } from './IncomeInvoicesTab';
import { AccountingDashboard } from './AccountingDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORDER_STATUSES = [
  "Pendiente de aceptación", "Aceptado", "Contactando", "Redactor", "Revisión", "Texto Aprobado", 
  "Modificaciones Solicitadas", "Medio", "Publicada",
  "Cancelada", "Consultando", "Incidencia", "Programado", "Espera Proforma"
];

const PAYMENT_STATUSES = ["Pendiente", "Pagado", "Parcial", "Cancelado"];

const STATUS_COLORS = {
  "Pendiente de aceptación": "bg-amber-100 text-amber-800 border border-amber-300",
  "Contactando": "bg-blue-100 text-blue-800",
  "Redactor": "bg-yellow-100 text-yellow-800",
  "Revisión": "bg-orange-100 text-orange-800",
  "Texto Aprobado": "bg-sky-100 text-sky-800 border border-sky-300",
  "Modificaciones Solicitadas": "bg-rose-100 text-rose-800 border border-rose-300",
  "Medio": "bg-purple-100 text-purple-800",
  "Publicada": "bg-green-200 text-green-900 border border-green-400 font-semibold",
  "Cancelada": "bg-red-100 text-red-800",
  "Consultando": "bg-cyan-100 text-cyan-800",
  "Incidencia": "bg-pink-100 text-pink-800",
  "Programado": "bg-indigo-100 text-indigo-800",
  "Espera Proforma": "bg-gray-100 text-gray-800",
};

const PAYMENT_COLORS = {
  "Pendiente": "bg-yellow-100 text-yellow-800",
  "Pagado": "bg-green-100 text-green-800",
  "Parcial": "bg-orange-100 text-orange-800",
  "Cancelado": "bg-red-100 text-red-800",
};

// Income invoice options
const INCOME_PAYMENT_METHODS = ["Paypal", "Tarjeta Caixa", "Caixa", "Tarjeta Wise", "Wise", "Payonner", "Transferencia bancaria"];
const INCOME_STATUSES = ["Cobrada", "A la espera de cobro"];
const INCOME_STATUS_COLORS = {
  "Cobrada": "bg-green-100 text-green-800",
  "A la espera de cobro": "bg-yellow-100 text-yellow-800",
};

// SortHeader component extracted outside main component
const SortHeader = ({ field, children, sortField, sortOrder, onSort }) => (
  <TableHead 
    className="cursor-pointer hover:bg-muted/50 select-none"
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      {children}
      {sortField === field ? (
        sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
      ) : (
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
      )}
    </div>
  </TableHead>
);

export const CRMTab = ({ language: langProp, defaultView = 'orders', onOrdersChange }) => {
  const { language, tx } = useAdminTranslation();
  const [activeView, setActiveView] = useState(defaultView);
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [writers, setWriters] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [expenseStats, setExpenseStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [expensePagination, setExpensePagination] = useState({ page: 1, total: 0, total_pages: 1 });
  
  // Sincronizar activeView con defaultView cuando cambia la prop
  useEffect(() => {
    setActiveView(defaultView);
  }, [defaultView]);
  
  // Search and filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');  // P, E, or '' for all
  const [expenseSearch, setExpenseSearch] = useState('');
  const [expensePaymentFilter, setExpensePaymentFilter] = useState('');
  
  // Dialog states
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [writerDialogOpen, setWriterDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingWriter, setEditingWriter] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Sort
  const [sortField, setSortField] = useState('order_number');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expenseSortField, setExpenseSortField] = useState('created_at');
  const [expenseSortOrder, setExpenseSortOrder] = useState('desc');
  
  // Form states
  const [writerForm, setWriterForm] = useState({ name: '', email: '', active: true });
  
  // Expense form
  const [expenseForm, setExpenseForm] = useState({
    invoice_number: '',
    order_id: '',
    order_number: 0,
    order_pvp: 0,
    order_pc: 0,
    invoice_date: '',
    provider: '',
    base_amount: 0,
    iva: 0,
    irpf: 0,
    total: 0,
    payment_method: '',
    status: 'No pagada',
    notes: '',
  });
  const [expenseFiles, setExpenseFiles] = useState([]);
  const [pendingExpenseFiles, setPendingExpenseFiles] = useState([]);
  const expenseFileInputRef = React.useRef(null);
  const [orderSelectOpen, setOrderSelectOpen] = useState(false);
  
  // Expense download states
  const [showExpenseDownloadDialog, setShowExpenseDownloadDialog] = useState(false);
  const [expenseDownloadDateFrom, setExpenseDownloadDateFrom] = useState('');
  const [expenseDownloadDateTo, setExpenseDownloadDateTo] = useState('');
  const [expenseDownloadPreset, setExpenseDownloadPreset] = useState('');
  
  // Income dialog state
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [incomeInvoices, setIncomeInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [incomeFiles, setIncomeFiles] = useState([]);
  const [pendingIncomeFiles, setPendingIncomeFiles] = useState([]);
  const incomeFileInputRef = React.useRef(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const [incomeForm, setIncomeForm] = useState({
    invoice_number: '',
    order_number: 0,
    client_name: '',
    client_nif: '',
    invoice_date: '',
    base_amount: 0,
    iva: 21,
    irpf: 0,
    total: 0,
    payment_method: '',
    status: 'A la espera de cobro',
    description: '',
    notes: ''
  });
  const [showIncomeDownloadDialog, setShowIncomeDownloadDialog] = useState(false);
  const [incomeDownloadDateFrom, setIncomeDownloadDateFrom] = useState('');
  const [incomeDownloadDateTo, setIncomeDownloadDateTo] = useState('');
  const [incomeDownloadPreset, setIncomeDownloadPreset] = useState('');
  
  // Products for search (from catalog)
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  
  // Combobox open states
  const [customerOpen, setCustomerOpen] = useState(false);
  const [writerOpen, setWriterOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  
  // Selected product details
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // File upload states
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingExpenseInvoice, setUploadingExpenseInvoice] = useState(false);
  const [orderFiles, setOrderFiles] = useState([]); // Files already uploaded (for editing)
  const [pendingFiles, setPendingFiles] = useState([]); // Files to upload on save (for new orders)
  const [expenseInvoiceFile, setExpenseInvoiceFile] = useState(null); // Expense invoice PDF
  const fileInputRef = React.useRef(null);
  const expenseInvoiceInputRef = React.useRef(null);
  
  // Orders import states
  const [importingOrders, setImportingOrders] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const ordersImportInputRef = React.useRef(null);
  
  // Expenses import states
  const [importingExpenses, setImportingExpenses] = useState(false);
  const [showExpensesImportDialog, setShowExpensesImportDialog] = useState(false);
  const [expensesImportResult, setExpensesImportResult] = useState(null);
  const expensesImportInputRef = React.useRef(null);
  
  // Income import states
  const [importingIncome, setImportingIncome] = useState(false);
  const [showIncomeImportDialog, setShowIncomeImportDialog] = useState(false);
  const [incomeImportResult, setIncomeImportResult] = useState(null);
  const incomeImportInputRef = React.useRef(null);
  
  // "Medio" status popup states
  const [showMedioPopup, setShowMedioPopup] = useState(false);
  const [medioPopupOrder, setMedioPopupOrder] = useState(null);
  const [medioPopupLanguage, setMedioPopupLanguage] = useState('es');
  const [medioCopied, setMedioCopied] = useState(false);
  
  // "Medio" document requirement dialog states
  const [showMedioDocDialog, setShowMedioDocDialog] = useState(false);
  const [medioDocOrder, setMedioDocOrder] = useState(null);
  const [medioDocUrl, setMedioDocUrl] = useState('');
  const [medioDocFiles, setMedioDocFiles] = useState([]); // Changed to array for multiple files
  const [uploadingMedioDoc, setUploadingMedioDoc] = useState(false);

  // Email preview / inline-edit dialog before status change
  const [emailPreview, setEmailPreview] = useState({
    open: false,
    loading: false,
    sending: false,
    available: true,
    orderId: null,
    status: '',
    toEmail: '',
    toName: '',
    fromEmail: '',
    replyTo: '',
    subject: '',
    html: '',
    extras: {},
    onConfirm: null, // (subject, html) => Promise
    onCancel: null,
    showHtmlEditor: false,
  });
  
  const [orderForm, setOrderForm] = useState({
    customer_id: '',
    customer_name: '',
    customer_email: '',
    external_order_number: '',
    product_id: '',
    product_url: '',
    web_email: '',
    price_type: 'normal',
    ask_topics: '',
    information: '',
    revision: '',
    published_url: '',
    revision_url: '',
    url_1: '',
    anchor_text_1: '',
    url_2: '',
    anchor_text_2: '',
    writer_instructions: '',
    status: 'Pendiente de aceptación',
    writer: '',
    writer_id: '',
    writer_email: '',
    order_date: '',
    final_date: '',
    send_date: '',
    deadline: '',
    pvp: 0,
    pc_sensitive: 0,
    pc: 0,
    invoice_income_number: '',
    invoice_expense_number: '',
    notified: false,
    payment_status: 'Pendiente',
    collection_date: '',
    invoice_date: '',
    payment_deadline: '',
    internal_notes: '',
    incident_reason: '',
    expense_invoice_file: null,  // For PDF upload
  });
  
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
  });

  const texts = {
    es: {
      orders: 'Pedidos',
      customers: 'Clientes',
      stats: 'Estadísticas',
      newOrder: 'Nuevo Pedido',
      newCustomer: 'Nuevo Cliente',
      search: 'Buscar...',
      orderNumber: 'Nº Pedido',
      customer: 'Cliente',
      productUrl: 'URL Producto',
      status: 'Estado',
      paymentStatus: 'Estado Pago',
      pvp: 'PVP',
      pc: 'PC',
      writer: 'Redactor',
      orderDate: 'F. Pedido',
      actions: 'Acciones',
      save: 'Guardar',
      cancel: 'Cancelar',
      allStatuses: 'Todos los estados',
      allPayments: 'Todos los pagos',
      export: 'Exportar',
      refresh: 'Actualizar',
      totalOrders: 'Total Pedidos',
      totalRevenue: 'Ingresos',
      totalCost: 'Costes',
      margin: 'Margen',
      noOrders: 'No hay pedidos',
      noCustomers: 'No hay clientes',
      customerNumber: 'Nº Cliente',
      externalOrderNumber: 'Nº P Exter',
      email: 'Email',
      phone: 'Teléfono',
      company: 'Empresa',
      name: 'Nombre',
      webEmail: 'Email Web',
      askTopics: 'Preguntar Temáticas',
      information: 'Información',
      revision: 'Revisión',
      publishedUrl: 'URL Publicada',
      finalDate: 'F. Final',
      sendDate: 'F. Envío',
      deadline: 'F. Límite',
      notified: 'Avisado',
      collectionDate: 'F. Cobro',
      invoiceDate: 'F. Factura',
      paymentDeadline: 'F. Límite Pago',
      notes: 'Notas',
      editOrder: 'Editar Pedido',
      editCustomer: 'Editar Cliente',
      page: 'Página',
      of: 'de',
      writers: 'Redactores',
      newWriter: 'Nuevo Redactor',
      editWriter: 'Editar Redactor',
      noWriters: 'No hay redactores',
      active: 'Activo',
      selectWriter: 'Seleccionar redactor',
      selectCustomer: 'Buscar cliente...',
      selectProduct: 'Buscar producto...',
      noCustomerFound: 'No se encontró cliente',
      noWriterFound: 'No se encontró redactor',
      noProductFound: 'No se encontró producto',
      priceType: 'Tipo de Precio',
      normal: 'Normal',
      sensible: 'Temática Sensible',
      invoiceIncome: 'Nº Factura Ingreso',
      invoiceExpense: 'Nº Factura Gasto',
      uploadExpenseInvoice: 'Subir Factura PDF',
      expenseInvoiceUploaded: 'Factura subida',
      product: 'Producto',
      salePriceLabel: 'Precio Venta',
      purchasePriceLabel: 'Precio Compra',
      // Expenses/Invoicing
      expenses: 'Facturación',
      newExpense: 'Añadir Gasto',
      editExpense: 'Editar Gasto',
      noExpenses: 'No hay gastos',
      invoiceNumber: 'Nº Factura',
      selectOrder: 'Buscar pedido...',
      noOrderFound: 'No se encontró pedido',
      expenseInvoiceDate: 'Fecha Factura Gasto',
      createdDate: 'Fecha Registro',
      provider: 'Proveedor',
      baseAmount: 'PVP sin IVA',
      iva: 'IVA',
      irpf: 'IRPF',
      total: 'Total',
      paymentMethod: 'Forma de Pago',
      totalExpenses: 'Total Gastos',
      totalBase: 'Total Base',
      viewExpense: 'Ver Factura',
      loadingExpense: 'Cargando factura...',
      noExpenseFound: 'No hay factura asociada',
      expenseStatus: 'Estado',
      statusPaid: 'Pagada',
      statusUnpaid: 'No pagada',
      statusPartial: 'Pagada 50%',
      statusProforma: 'Con proforma',
      requiredFields: 'Campos obligatorios',
      invoiceNumberRequired: 'El Nº Factura es obligatorio',
      orderRequired: 'Debe seleccionar un pedido',
      providerRequired: 'El Proveedor es obligatorio',
      baseAmountRequired: 'El PVP sin IVA es obligatorio',
      statusRequired: 'El Estado es obligatorio',
      invoiceDateRequired: 'La Fecha de Factura es obligatoria',
      paymentMethodRequired: 'La Forma de Pago es obligatoria',
      import: 'Importar',
      importOrders: 'Importador de Pedidos',
      importOrdersDesc: 'Sube un archivo Excel (.xlsx) con los pedidos a importar',
      importExpenses: 'Importar Gastos',
      importExpensesDesc: 'Sube un archivo Excel (.xlsx) con las facturas de gasto a importar',
      importIncome: 'Importar Ingresos',
      importIncomeDesc: 'Sube un archivo Excel (.xlsx) con las facturas de ingreso a importar',
      selectFile: 'Seleccionar archivo',
      importSuccess: 'Importación completada',
      importError: 'Error en la importación',
      ordersImported: 'pedidos importados',
      ordersUpdated: 'pedidos actualizados',
      ordersSkipped: 'pedidos omitidos',
      expensesImported: 'gastos importados',
      expensesUpdated: 'gastos actualizados',
      expensesSkipped: 'gastos omitidos',
      incomeImported: 'ingresos importados',
      incomeUpdated: 'ingresos actualizados',
      incomeSkipped: 'ingresos omitidos',
      downloadTemplate: 'Descargar plantilla',
    },
    en: {
      orders: 'Orders',
      customers: 'Customers',
      stats: 'Statistics',
      newOrder: 'New Order',
      newCustomer: 'New Customer',
      search: 'Search...',
      orderNumber: 'Order #',
      customer: 'Customer',
      productUrl: 'Product URL',
      status: 'Status',
      paymentStatus: 'Payment Status',
      pvp: 'PVP',
      pc: 'PC',
      writer: 'Writer',
      orderDate: 'Order Date',
      actions: 'Actions',
      save: 'Save',
      cancel: 'Cancel',
      allStatuses: 'All statuses',
      allPayments: 'All payments',
      export: 'Export',
      refresh: 'Refresh',
      totalOrders: 'Total Orders',
      totalRevenue: 'Revenue',
      totalCost: 'Costs',
      margin: 'Margin',
      noOrders: 'No orders',
      noCustomers: 'No customers',
      customerNumber: 'Customer #',
      externalOrderNumber: 'External Order #',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      name: 'Name',
      webEmail: 'Web Email',
      askTopics: 'Ask Topics',
      information: 'Information',
      revision: 'Revisión',
      publishedUrl: 'Published URL',
      finalDate: 'Final Date',
      sendDate: 'Send Date',
      deadline: 'Deadline',
      notified: 'Notified',
      collectionDate: 'Collection Date',
      invoiceDate: 'Invoice Date',
      paymentDeadline: 'Payment Deadline',
      notes: 'Notes',
      editOrder: 'Edit Order',
      editCustomer: 'Edit Customer',
      page: 'Page',
      of: 'of',
      writers: 'Writers',
      newWriter: 'New Writer',
      editWriter: 'Edit Writer',
      noWriters: 'No writers',
      active: 'Active',
      selectWriter: 'Select writer',
      selectCustomer: 'Search customer...',
      selectProduct: 'Search product...',
      noCustomerFound: 'No customer found',
      noWriterFound: 'No writer found',
      noProductFound: 'No product found',
      priceType: 'Price Type',
      normal: 'Normal',
      sensible: 'Sensitive Topic',
      invoiceIncome: 'Income Invoice #',
      invoiceExpense: 'Expense Invoice #',
      uploadExpenseInvoice: 'Upload Invoice PDF',
      expenseInvoiceUploaded: 'Invoice uploaded',
      product: 'Product',
      salePriceLabel: 'Sale Price',
      purchasePriceLabel: 'Purchase Price',
      // Expenses/Invoicing
      expenses: 'Invoicing',
      newExpense: 'Add Expense',
      editExpense: 'Edit Expense',
      noExpenses: 'No expenses',
      invoiceNumber: 'Invoice #',
      selectOrder: 'Search order...',
      noOrderFound: 'No order found',
      expenseInvoiceDate: 'Expense Invoice Date',
      createdDate: 'Created Date',
      provider: 'Provider',
      baseAmount: 'Amount excl. VAT',
      iva: 'VAT',
      irpf: 'IRPF',
      total: 'Total',
      paymentMethod: 'Payment Method',
      totalExpenses: 'Total Expenses',
      totalBase: 'Total Base',
      viewExpense: 'View Invoice',
      loadingExpense: 'Loading invoice...',
      noExpenseFound: 'No invoice found',
      expenseStatus: 'Status',
      statusPaid: 'Paid',
      statusUnpaid: 'Unpaid',
      statusPartial: '50% Paid',
      statusProforma: 'With Proforma',
      requiredFields: 'Required fields',
      invoiceNumberRequired: 'Invoice # is required',
      orderRequired: 'Must select an order',
      providerRequired: 'Provider is required',
      baseAmountRequired: 'Amount excl. VAT is required',
      statusRequired: 'Status is required',
      invoiceDateRequired: 'Invoice date is required',
      paymentMethodRequired: 'Payment method is required',
      import: 'Import',
      importOrders: 'Order Importer',
      importOrdersDesc: 'Upload an Excel file (.xlsx) with orders to import',
      importExpenses: 'Import Expenses',
      importExpensesDesc: 'Upload an Excel file (.xlsx) with expense invoices to import',
      importIncome: 'Import Income',
      importIncomeDesc: 'Upload an Excel file (.xlsx) with income invoices to import',
      selectFile: 'Select file',
      importSuccess: 'Import completed',
      importError: 'Import error',
      ordersImported: 'orders imported',
      ordersUpdated: 'orders updated',
      ordersSkipped: 'orders skipped',
      expensesImported: 'expenses imported',
      expensesUpdated: 'expenses updated',
      expensesSkipped: 'expenses skipped',
      incomeImported: 'income invoices imported',
      incomeUpdated: 'income invoices updated',
      incomeSkipped: 'income invoices skipped',
      downloadTemplate: 'Download template',
    }
  };
  
  const t = texts[language] || texts.es;

  // Generate "Medio" email template in different languages
  const generateMedioTemplate = (order, templateLanguage) => {
    const productName = order?.product_url || '[NOMBRE DEL MEDIO]';
    const price = order?.product_pvp || order?.pvp || 0;
    
    const templates = {
      es: `Hola,

Les adjunto un artículo para su publicación en ${productName}, así mismo se acompaña una imagen para que ilustre dicho artículo. Es importante que el enlace sea do-follow, permanente y que el artículo no aparezca con ninguna etiqueta de publicidad.

El precio que tenemos anotado es: ${price}€

-¿Podría confirmar si ha recibido el artículo?

Así también, adjuntos mis datos de facturación:

PUROLINK SL
ESB09704610
C/ Santiago 258 bajo izda
28822 Coslada, Madrid
España

Muchas gracias.

Recibe un cordial saludo.`,

      en: `Hello,

I am attaching an article for publication on ${productName}, along with an image to illustrate the article. It is important that the link is do-follow, permanent and that the article does not appear with any advertising label.

The price we have noted is: €${price}

-Could you confirm if you have received the article?

Also, attached are my billing details:

PUROLINK SL
ESB09704610
C/ Santiago 258 bajo izda
28822 Coslada, Madrid
Spain

Thank you very much.

Best regards.`,

      fr: `Bonjour,

Je vous envoie ci-joint un article pour publication sur ${productName}, accompagné d'une image pour illustrer ledit article. Il est important que le lien soit do-follow, permanent et que l'article n'apparaisse avec aucune étiquette publicitaire.

Le prix que nous avons noté est: ${price}€

-Pourriez-vous confirmer si vous avez bien reçu l'article ?

Ci-joint également mes coordonnées de facturation:

PUROLINK SL
ESB09704610
C/ Santiago 258 bajo izda
28822 Coslada, Madrid
Espagne

Merci beaucoup.

Cordialement.`,

      de: `Hallo,

ich sende Ihnen anbei einen Artikel zur Veröffentlichung auf ${productName}, zusammen mit einem Bild zur Illustration des Artikels. Es ist wichtig, dass der Link do-follow und permanent ist und dass der Artikel mit keinem Werbelabel erscheint.

Der notierte Preis ist: ${price}€

-Könnten Sie bestätigen, ob Sie den Artikel erhalten haben?

Anbei auch meine Rechnungsdaten:

PUROLINK SL
ESB09704610
C/ Santiago 258 bajo izda
28822 Coslada, Madrid
Spanien

Vielen Dank.

Mit freundlichen Grüßen.`,

      it: `Buongiorno,

vi invio in allegato un articolo per la pubblicazione su ${productName}, insieme a un'immagine per illustrare l'articolo. È importante che il link sia do-follow, permanente e che l'articolo non appaia con nessuna etichetta pubblicitaria.

Il prezzo che abbiamo annotato è: ${price}€

-Potrebbe confermare se ha ricevuto l'articolo?

In allegato anche i miei dati di fatturazione:

PUROLINK SL
ESB09704610
C/ Santiago 258 bajo izda
28822 Coslada, Madrid
Spagna

Grazie mille.

Cordiali saluti.`,

      pt: `Olá,

Envio em anexo um artigo para publicação em ${productName}, acompanhado de uma imagem para ilustrar o artigo. É importante que o link seja do-follow, permanente e que o artigo não apareça com nenhum rótulo de publicidade.

O preço que temos anotado é: ${price}€

-Poderia confirmar se recebeu o artigo?

Anexo também os meus dados de faturação:

PUROLINK SL
ESB09704610
C/ Santiago 258 bajo izda
28822 Coslada, Madrid
Espanha

Muito obrigado.

Cordiais saudações.`
    };
    
    return templates[templateLanguage] || templates.es;
  };

  const handleCopyMedioTemplate = async () => {
    const template = generateMedioTemplate(medioPopupOrder, medioPopupLanguage);
    try {
      await navigator.clipboard.writeText(template);
      setMedioCopied(true);
      toast.success(tx('Plantilla copiada al portapapeles', 'Template copied to clipboard'));
      setTimeout(() => setMedioCopied(false), 2000);
    } catch (err) {
      toast.error(tx('Error al copiar', 'Error copying'));
    }
  };

  const PAYMENT_METHODS = ['Paypal', 'Tarjeta Caixa', 'Caixa', 'Tarjeta Wise', 'Wise', 'Payonner'];
  const EXPENSE_STATUSES = ['Pagada', 'No pagada', 'Pagada 50%', 'Con proforma'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOrders(),
        fetchCustomers(),
        fetchWriters(),
        fetchStats(),
        fetchExpenses(),
        fetchExpenseStats()
      ]);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      if (orderTypeFilter) params.append('order_type', orderTypeFilter);
      params.append('sort_by', sortField);
      params.append('sort_order', sortOrder);
      
      const response = await axios.get(`${API}/admin/orders?${params.toString()}`);
      setOrders(response.data.orders);
      setPagination(response.data.pagination);
      setSelectedOrders([]); // Clear selection on fetch
      
      // Notify parent about order changes (for badge update)
      if (onOrdersChange) {
        onOrdersChange();
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Order selection handlers
  const handleSelectOrder = (orderId, checked) => {
    if (checked) {
      setSelectedOrders(prev => [...prev, orderId]);
    } else {
      setSelectedOrders(prev => prev.filter(id => id !== orderId));
    }
  };

  const handleSelectAllOrders = (checked) => {
    if (checked) {
      setSelectedOrders(orders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleBulkDeleteOrders = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Selecciona al menos un pedido');
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar ${selectedOrders.length} pedido(s)?`)) {
      return;
    }

    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/admin/orders/bulk-delete`, selectedOrders);
      toast.success(response.data.message);
      setSelectedOrders([]);
      fetchOrders();
    } catch (error) {
      console.error('Error deleting orders:', error);
      toast.error('Error al eliminar los pedidos');
    } finally {
      setBulkDeleting(false);
    }
  };

  const fetchExpenses = async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (expenseSearch) params.append('search', expenseSearch);
      if (expensePaymentFilter && expensePaymentFilter !== 'all') params.append('payment_method', expensePaymentFilter);
      params.append('sort_field', expenseSortField);
      params.append('sort_order', expenseSortOrder);
      
      const response = await axios.get(`${API}/admin/expenses?${params.toString()}`);
      setExpenses(response.data.expenses);
      setExpensePagination({
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages
      });
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchExpenseStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/expenses/stats`);
      setExpenseStats(response.data);
    } catch (error) {
      console.error('Error fetching expense stats:', error);
    }
  };

  const fetchCustomers = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/admin/customers?page=${page}&limit=50`);
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchWriters = async () => {
    try {
      const response = await axios.get(`${API}/admin/writers`);
      setWriters(response.data.writers);
    } catch (error) {
      console.error('Error fetching writers:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Search products for dropdown
  const searchProducts = async (query) => {
    if (!query || query.length < 1) {
      setProducts([]);
      return;
    }
    setProductSearchLoading(true);
    try {
      const response = await axios.get(`${API}/admin/products/search?q=${encodeURIComponent(query)}&limit=20`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    } finally {
      setProductSearchLoading(false);
    }
  };

  // Handle product selection - auto-fill prices based on type
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    const priceType = orderForm.price_type || 'normal';
    const pvp = priceType === 'sensible' ? (product.premium_sale_price || 0) : (product.pvp || 0);
    const pc = priceType === 'sensible' ? (product.premium_purchase_price || 0) : (product.pc || 0);
    
    setOrderForm(prev => ({
      ...prev,
      product_id: product.id,
      product_url: product.website,
      web_email: product.web_email || '',
      pvp: pvp,
      pc: pc,
    }));
    setProductOpen(false);
  };

  // Handle price type change - update prices
  const handlePriceTypeChange = (newType) => {
    setOrderForm(prev => {
      let newPvp = prev.pvp;
      let newPc = prev.pc;
      
      if (selectedProduct) {
        newPvp = newType === 'sensible' ? (selectedProduct.premium_sale_price || 0) : (selectedProduct.pvp || 0);
        newPc = newType === 'sensible' ? (selectedProduct.premium_purchase_price || 0) : (selectedProduct.pc || 0);
      }
      
      return {
        ...prev,
        price_type: newType,
        pvp: newPvp,
        pc: newPc,
      };
    });
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setOrderForm(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_email: customer.email || '',
    }));
    setCustomerOpen(false);
  };

  // Handle writer selection
  const handleWriterSelect = (writer) => {
    setOrderForm(prev => ({
      ...prev,
      writer_id: writer.id,
      writer: writer.name,
      writer_email: writer.email,
    }));
    setWriterOpen(false);
  };

  // File upload handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const allowedExtensions = ['.doc', '.docx', '.pdf', '.txt', '.xls', '.xlsx'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      toast.error(tx(
        `Tipo de archivo no permitido. Permitidos: ${allowedExtensions.join(', ')}`,
        `File type not allowed. Allowed: ${allowedExtensions.join(', ')}`
      ));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    if (editingOrder) {
      // If editing, upload immediately
      handleFileUpload(file, editingOrder.id);
    } else {
      // If creating new order, add to pending files
      setPendingFiles(prev => [...prev, {
        file: file,
        original_name: file.name,
        size: file.size,
        extension: fileExt
      }]);
      toast.success(tx('Archivo añadido (se subirá al guardar)', 'File added (will upload on save)'));
    }
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileUpload = async (file, orderId) => {
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/orders/${orderId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(tx('Archivo subido correctamente', 'File uploaded successfully'));
      setOrderFiles(prev => [...prev, response.data.file]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading file');
    } finally {
      setUploadingFile(false);
    }
  };

  const uploadPendingFiles = async (orderId) => {
    for (const pendingFile of pendingFiles) {
      const formData = new FormData();
      formData.append('file', pendingFile.file);
      try {
        await axios.post(`${API}/admin/orders/${orderId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    setPendingFiles([]);
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownloadFile = async (orderId, filename, originalName) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}/files/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(tx('Error descargando archivo', 'Error downloading file'));
    }
  };

  const handleDeleteFile = async (orderId, filename) => {
    if (!window.confirm(tx('¿Eliminar este archivo?', 'Delete this file?'))) return;
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}/files/${filename}`);
      toast.success(tx('Archivo eliminado', 'File deleted'));
      setOrderFiles(prev => prev.filter(f => f.filename !== filename));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting file');
    }
  };

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    fetchOrders(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrders(1);
  };

  const resetOrderForm = () => {
    setOrderForm({
      customer_id: '',
      customer_name: '',
      customer_email: '',
      external_order_number: '',
      product_id: '',
      product_url: '',
      web_email: '',
      price_type: 'normal',
      ask_topics: '',
      information: '',
      revision: '',
      published_url: '',
      revision_url: '',
      url_1: '',
      anchor_text_1: '',
      url_2: '',
      anchor_text_2: '',
      writer_instructions: '',
      status: 'Pendiente de aceptación',
      writer: '',
      writer_id: '',
      writer_email: '',
      order_date: '',
      final_date: '',
      send_date: '',
      deadline: '',
      pvp: 0,
      pc_sensitive: 0,
      pc: 0,
      invoice_income_number: '',
      invoice_expense_number: '',
      notified: false,
      payment_status: 'Pendiente',
      collection_date: '',
      invoice_date: '',
      payment_deadline: '',
      internal_notes: '',
      incident_reason: '',
      // Internal and authority links
      add_internal_link: false,
      internal_link_url: '',
      internal_link_anchor: '',
      add_authority_link: false,
      authority_link_url: '',
      authority_link_anchor: '',
    });
    setSelectedProduct(null);
    setOrderFiles([]);
    setPendingFiles([]);
  };

  const openOrderDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      setOrderFiles(order.attached_files || []);
      setPendingFiles([]);
      setOrderForm({
        customer_id: order.customer_id || '',
        customer_name: order.customer_name || '',
        customer_email: order.customer_email || '',
        external_order_number: order.external_order_number || '',
        product_id: order.product_id || '',
        product_url: order.product_url || '',
        web_email: order.web_email || '',
        price_type: order.price_type || 'normal',
        ask_topics: order.ask_topics || '',
        information: order.information || '',
        revision: order.revision || '',
        published_url: order.published_url || '',
        revision_url: order.revision_url || '',
        url_1: order.url_1 || '',
        anchor_text_1: order.anchor_text_1 || order.anchor_1 || '',
        url_2: order.url_2 || '',
        anchor_text_2: order.anchor_text_2 || order.anchor_2 || '',
        writer_instructions: order.description || order.writer_instructions || '',
        word_count: order.word_count || '',
        status: order.status || 'Pendiente de aceptación',
        writer: order.writer || '',
        writer_id: order.writer_id || '',
        writer_email: order.writer_email || '',
        order_date: order.order_date ? order.order_date.split('T')[0] : '',
        final_date: order.final_date ? order.final_date.split('T')[0] : '',
        send_date: order.send_date ? order.send_date.split('T')[0] : '',
        deadline: order.deadline ? order.deadline.split('T')[0] : '',
        pvp: order.pvp || 0,
        pc: order.pc || 0,
        pc_sensitive: order.pc_sensitive || 0,
        invoice_income_number: order.invoice_income_number || '',
        invoice_expense_number: order.invoice_expense_number || '',
        notified: order.notified || false,
        payment_status: order.payment_status || 'Pendiente',
        collection_date: order.collection_date ? order.collection_date.split('T')[0] : '',
        invoice_date: order.invoice_date ? order.invoice_date.split('T')[0] : '',
        payment_deadline: order.payment_deadline ? order.payment_deadline.split('T')[0] : '',
        internal_notes: order.internal_notes || '',
        incident_reason: order.incident_reason || '',
        expense_invoice_file: order.expense_invoice_file || null,
        // Internal and authority links
        add_internal_link: order.add_internal_link || false,
        internal_link_url: order.internal_link_url || '',
        internal_link_anchor: order.internal_link_anchor || '',
        add_authority_link: order.add_authority_link || false,
        authority_link_url: order.authority_link_url || '',
        authority_link_anchor: order.authority_link_anchor || '',
      });
      setExpenseInvoiceFile(null); // Reset file selection when editing
      setSelectedProduct(null);
    } else {
      setEditingOrder(null);
      resetOrderForm();
    }
    setOrderDialogOpen(true);
  };

  const handleSaveOrder = async () => {
    // Validación: Si estado es "Redactor", obligar a seleccionar un redactor
    if (orderForm.status === 'Redactor' && !orderForm.writer_id) {
      toast.error(tx('Debes seleccionar un redactor cuando el estado es "Redactor"', 'You must select a writer when status is "Redactor"'));
      return;
    }
    
    // Validación: Si estado es "Publicada", obligar a poner la URL publicada
    if (orderForm.status === 'Publicada' && !orderForm.published_url) {
      toast.error(tx('Debes introducir la URL publicada cuando el estado es "Publicada"', 'You must enter the published URL when status is "Published"'));
      return;
    }
    
    // Validación: Si estado es "Incidencia", OBLIGATORIO poner el motivo
    if (orderForm.status === 'Incidencia' && !orderForm.incident_reason) {
      toast.error(tx('Debes añadir el motivo de la incidencia para informar al cliente', 'You must add the incident reason to inform the client'));
      return;
    }
    
    // Validación: Si estado es "Medio", bloquear - debe usar el diálogo de documento
    if (orderForm.status === 'Medio' && editingOrder) {
      toast.error(tx('Para cambiar a "Medio" debes usar el selector de estado que abrirá el diálogo de documento obligatorio', 'To change to "Medio" you must use the status selector which will open the mandatory document dialog'));
      return;
    }
    
    // Estado "Revisión" — el enlace de Drive es OPCIONAL: el redactor puede subir
    // archivos en su lugar a través del formulario seguro `/writer-submit/{token}`.
    // No bloqueamos el guardado por la ausencia de revision_url.
    
    try {
      const data = { ...orderForm };
      
      // Auto-set order_date to current date for new orders
      if (!editingOrder) {
        data.order_date = new Date().toISOString();
      }
      
      // Convert date strings to ISO format
      ['order_date', 'final_date', 'send_date', 'deadline', 'collection_date', 'invoice_date', 'payment_deadline'].forEach(key => {
        if (data[key] && typeof data[key] === 'string' && !data[key].includes('T')) {
          data[key] = new Date(data[key]).toISOString();
        } else if (!data[key]) {
          delete data[key];
        }
      });

      let savedOrderId = null;
      
      if (editingOrder) {
        // Detectar si el estado cambió para decidir si enviar email
        const statusChanged = editingOrder.status !== orderForm.status;
        // Solo enviar email si el estado cambió (skip_email: false cuando hay cambio de estado)
        await axios.put(`${API}/admin/orders/${editingOrder.id}`, { ...data, skip_email: !statusChanged });
        savedOrderId = editingOrder.id;
        
        if (statusChanged) {
          toast.success(tx(`Pedido actualizado y estado cambiado a "${orderForm.status}"`, `Order updated and status changed to "${orderForm.status}"`));
        } else {
          toast.success(tx('Pedido actualizado', 'Order updated'));
        }
      } else {
        const response = await axios.post(`${API}/admin/orders`, data);
        savedOrderId = response.data.id;
        
        // Upload pending files if any
        if (pendingFiles.length > 0 && savedOrderId) {
          await uploadPendingFiles(savedOrderId);
          toast.success(tx('Pedido creado con archivos', 'Order created with files'));
        } else {
          toast.success(tx('Pedido creado', 'Order created'));
        }
      }
      
      // Upload expense invoice PDF if selected
      if (expenseInvoiceFile && savedOrderId) {
        setUploadingExpenseInvoice(true);
        const formData = new FormData();
        formData.append('file', expenseInvoiceFile);
        try {
          await axios.post(`${API}/admin/orders/${savedOrderId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          // Update order with the expense invoice filename
          await axios.put(`${API}/admin/orders/${savedOrderId}`, {
            expense_invoice_file: expenseInvoiceFile.name
          });
          toast.success(tx('Factura de gasto subida', 'Expense invoice uploaded'));
        } catch (uploadError) {
          toast.warning(tx('Error al subir factura de gasto', 'Error uploading expense invoice'));
        } finally {
          setUploadingExpenseInvoice(false);
          setExpenseInvoiceFile(null);
        }
      }
      
      // Enviar automáticamente al redactor si el estado es "Redactor" y hay un redactor seleccionado
      if (orderForm.status === 'Redactor' && orderForm.writer_id && savedOrderId) {
        try {
          await axios.post(`${API}/admin/orders/${savedOrderId}/send-to-writer`);
          toast.success(tx('Enviado al redactor automáticamente', 'Automatically sent to writer'));
        } catch (sendError) {
          toast.warning(tx('Pedido guardado, pero error al enviar al redactor', 'Order saved, but error sending to writer'));
        }
      }
      
      setOrderDialogOpen(false);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(tx('¿Eliminar este pedido?', 'Delete this order?'))) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      toast.success(tx('Pedido eliminado', 'Order deleted'));
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting order');
    }
  };

  const handleSendToWriter = async (orderId) => {
    try {
      const response = await axios.post(`${API}/admin/orders/${orderId}/send-to-writer`);
      toast.success(response.data.message || (tx('Enviado al redactor', 'Sent to writer')));
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Error al enviar', 'Error sending')));
    }
  };

  // Open the email preview / inline-edit dialog before applying a status change.
  // Behaviour:
  //  - Calls POST /admin/orders/{id}/preview-email to render what would be sent.
  //  - If the status doesn't trigger any email (`available=false`) → skip dialog and
  //    invoke `onConfirm(null, null)` directly so callers can proceed with the raw
  //    status update.
  //  - If available → opens dialog. On confirm: calls `onConfirm(subject, html)`
  //    which is responsible for hitting /change-status-with-email and refreshing.
  const openEmailPreview = React.useCallback(async ({ orderId, status, extras = {}, onConfirm }) => {
    setEmailPreview(prev => ({
      ...prev,
      open: true,
      loading: true,
      sending: false,
      orderId,
      status,
      extras,
      onConfirm,
      onCancel: null,
      showHtmlEditor: false,
    }));
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${orderId}/preview-email`,
        { status, ...extras }
      );
      if (!data.available) {
        // No email for this status — close dialog and proceed silently
        setEmailPreview(prev => ({ ...prev, open: false, loading: false }));
        if (onConfirm) await onConfirm(null, null);
        return;
      }
      setEmailPreview(prev => ({
        ...prev,
        loading: false,
        available: true,
        toEmail: data.to_email || '',
        toName: data.to_name || '',
        fromEmail: data.from_email || '',
        replyTo: data.reply_to || '',
        subject: data.subject || '',
        html: data.html || '',
      }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Error generando vista previa';
      toast.error(msg);
      setEmailPreview(prev => ({ ...prev, open: false, loading: false }));
    }
  }, []);

  const closeEmailPreview = () => setEmailPreview(prev => ({ ...prev, open: false }));

  const confirmEmailPreviewSend = async () => {
    if (!emailPreview.onConfirm) {
      closeEmailPreview();
      return;
    }
    setEmailPreview(prev => ({ ...prev, sending: true }));
    try {
      await emailPreview.onConfirm(emailPreview.subject, emailPreview.html);
      setEmailPreview(prev => ({ ...prev, open: false, sending: false }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Error enviando email';
      toast.error(msg);
      setEmailPreview(prev => ({ ...prev, sending: false }));
    }
  };

  const handleStatusChange = async (orderId, newStatus, order = null) => {
    // Block "Incidencia" status change from quick selector - must use edit form
    if (newStatus === 'Incidencia') {
      toast.error(tx(
        'Para cambiar a "Incidencia" debes editar el pedido y completar el motivo de la incidencia',
        'To change to "Incidencia" you must edit the order and fill in the incident reason'
      ), { duration: 5000 });
      return;
    }
    
    // Block "Cancelada" status change from quick selector - must use edit form for proper refund handling
    if (newStatus === 'Cancelada') {
      toast.error(tx(
        'Para cancelar un pedido debes editarlo desde el formulario',
        'To cancel an order you must edit it from the form'
      ), { duration: 5000 });
      return;
    }
    
    // Block "Publicada" status change from quick selector - must use edit form to add published URL
    if (newStatus === 'Publicada') {
      toast.error(tx(
        'Para cambiar a "Publicada" debes editar el pedido e incluir la URL de publicación',
        'To change to "Publicada" you must edit the order and include the published URL'
      ), { duration: 5000 });
      return;
    }
    
    // Block "Redactor" status change from quick selector - must use edit form to select writer and fill data
    if (newStatus === 'Redactor') {
      toast.error(tx(
        'Para cambiar a "Redactor" debes editar el pedido, seleccionar un redactor y completar los datos de la redacción (URLs, anchors, instrucciones)',
        'To change to "Redactor" you must edit the order, select a writer and fill in the writing data (URLs, anchors, instructions)'
      ), { duration: 6000 });
      return;
    }
    
    // For "Medio" status, show document requirement dialog first
    if (newStatus === 'Medio') {
      console.log('Medio status selected, showing dialog');
      // Get the order object if not passed
      const orderData = order || orders.find(o => o.id === orderId);
      console.log('Order data:', orderData);
      if (orderData) {
        setMedioDocOrder(orderData);
        setMedioDocUrl('');
        setMedioDocFiles([]);
        setShowMedioDocDialog(true);
        return;
      } else {
        console.error('No order data found for Medio dialog');
      }
    }
    
    try {
      // Show preview/edit modal before applying — admin can edit subject/HTML or skip.
      await openEmailPreview({
        orderId,
        status: newStatus,
        extras: {},
        onConfirm: async (editedSubject, editedHtml) => {
          if (editedSubject != null && editedHtml != null) {
            await axios.post(`${API}/admin/orders/${orderId}/change-status-with-email`, {
              status: newStatus,
              subject: editedSubject,
              html: editedHtml,
            });
          } else {
            // No email for this status — fall back to plain PATCH
            await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
          }
          toast.success(tx('Estado actualizado', 'Status updated'));
          await fetchOrders();
          await fetchStats();
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating status');
    }
  };
  
  // Handle "Medio" status change with document
  const handleMedioStatusWithDocument = async () => {
    if (!medioDocOrder) return;
    
    // Customer files attached at checkout — already in storage, just include their filenames
    const customerFiles = [
      medioDocOrder.article_file,
      medioDocOrder.image_file_1,
      medioDocOrder.image_file_2,
    ].filter(Boolean);

    // Validate that at least one document source is provided (URL, new files, or pre-attached customer files)
    const hasUrl = medioDocUrl.trim().length > 0;
    const hasFiles = medioDocFiles.length > 0;
    const hasCustomerFiles = customerFiles.length > 0;

    if (!hasUrl && !hasFiles && !hasCustomerFiles) {
      toast.error(tx('Debes incluir al menos un enlace de Drive o subir archivos', 'You must include at least a Drive link or upload files'));
      return;
    }
    
    setUploadingMedioDoc(true);
    
    try {
      // Upload all NEW files and collect their URLs
      let uploadedFileUrls = [];
      
      if (hasFiles) {
        console.log(`Uploading ${medioDocFiles.length} files for Medio status...`);
        for (const file of medioDocFiles) {
          const formData = new FormData();
          formData.append('file', file);
          console.log(`Uploading file: ${file.name}`);
          const uploadResponse = await axios.post(`${API}/admin/orders/${medioDocOrder.id}/upload`, formData);
          console.log('Upload response:', uploadResponse.data);
          // Get filename from response - it's inside file object
          const fileUrl = uploadResponse.data.file?.filename || uploadResponse.data.filename || uploadResponse.data.file_url;
          console.log(`Extracted filename: ${fileUrl}`);
          if (fileUrl) {
            uploadedFileUrls.push(fileUrl);
          }
        }
        console.log('All uploaded file URLs:', uploadedFileUrls);
      }
      
      // Combine: Drive URL + customer's checkout files + admin's new uploads
      let allDocuments = [];
      if (hasUrl) {
        allDocuments.push(medioDocUrl.trim());
      }
      // Customer files from checkout (already on disk — just reference filename)
      allDocuments = [...allDocuments, ...customerFiles];
      // Admin's new uploads
      allDocuments = [...allDocuments, ...uploadedFileUrls];
      
      // Join all document URLs with separator
      const documentUrl = allDocuments.join(' | ');
      const documentType = hasUrl && (hasFiles || hasCustomerFiles)
        ? 'mixed'
        : (hasUrl ? 'drive' : 'file');
      
      console.log('Final medio_document_url:', documentUrl);
      console.log('Document type:', documentType);
      
      // Update order with document URL and change status to "Medio"
      await axios.put(`${API}/admin/orders/${medioDocOrder.id}`, {
        status: 'Medio',
        medio_document_url: documentUrl,
        medio_document_type: documentType
      });
      
      // Show detailed success message
      const detailMessage = allDocuments.map((doc, i) => 
        doc.startsWith('http') ? `• Enlace Drive` : `• Archivo: ${doc}`
      ).join('\n');
      
      toast.success(
        `Estado actualizado a Medio\n${allDocuments.length} documento(s) enviado(s):\n${detailMessage}`,
        { duration: 5000 }
      );
      
      // Close document dialog and reset
      setShowMedioDocDialog(false);
      setMedioDocUrl('');
      setMedioDocFiles([]);
      
      // Refresh data
      await fetchOrders();
      await fetchStats();
      
      // Popup de plantilla removido - ya no se muestra automáticamente
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating status');
    } finally {
      setUploadingMedioDoc(false);
    }
  };

  const handleExportOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      
      const response = await axios.get(`${API}/admin/orders/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `pedidos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(tx('Exportación completada', 'Export completed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error exporting');
    }
  };

  // Import orders from Excel
  const handleImportOrders = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingOrders(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/orders/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      setShowImportDialog(true);
      toast.success(t.importSuccess);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.importError);
    } finally {
      setImportingOrders(false);
      // Reset file input
      if (ordersImportInputRef.current) {
        ordersImportInputRef.current.value = '';
      }
    }
  };

  const handleDownloadOrdersTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'plantilla_pedidos.xlsx';
      link.click();
    } catch (error) {
      toast.error(tx('Error descargando plantilla', 'Error downloading template'));
    }
  };

  // Expenses import functions
  const handleImportExpenses = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingExpenses(true);
    setExpensesImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/expenses/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setExpensesImportResult(response.data);
      setShowExpensesImportDialog(true);
      toast.success(t.importSuccess);
      fetchExpenses();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.importError);
    } finally {
      setImportingExpenses(false);
      if (expensesImportInputRef.current) {
        expensesImportInputRef.current.value = '';
      }
    }
  };

  const handleDownloadExpensesTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/expenses/template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'plantilla_facturas_gastos.xlsx';
      link.click();
    } catch (error) {
      toast.error(tx('Error descargando plantilla', 'Error downloading template'));
    }
  };

  // Income import functions
  const handleImportIncome = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingIncome(true);
    setIncomeImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/income-invoices/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setIncomeImportResult(response.data);
      setShowIncomeImportDialog(true);
      toast.success(t.importSuccess);
      fetchIncomeInvoices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.importError);
    } finally {
      setImportingIncome(false);
      if (incomeImportInputRef.current) {
        incomeImportInputRef.current.value = '';
      }
    }
  };

  const handleDownloadIncomeTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/income-invoices/template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'plantilla_facturas_ingresos.xlsx';
      link.click();
    } catch (error) {
      toast.error(tx('Error descargando plantilla', 'Error downloading template'));
    }
  };

  // Customer functions
  const resetCustomerForm = () => {
    setCustomerForm({ name: '', email: '', phone: '', company: '', nif: '', billing_address: '', notes: '' });
  };

  const openCustomerDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setCustomerForm({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        company: customer.company || '',
        nif: customer.nif || '',
        billing_address: customer.billing_address || '',
        notes: customer.notes || '',
      });
    } else {
      setEditingCustomer(null);
      resetCustomerForm();
    }
    setCustomerDialogOpen(true);
  };

  const handleSaveCustomer = async () => {
    try {
      if (editingCustomer) {
        await axios.put(`${API}/admin/customers/${editingCustomer.id}`, customerForm);
        toast.success(tx('Cliente actualizado', 'Customer updated'));
      } else {
        await axios.post(`${API}/admin/customers`, customerForm);
        toast.success(tx('Cliente creado', 'Customer created'));
      }
      setCustomerDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm(tx('¿Eliminar este cliente?', 'Delete this customer?'))) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}`);
      toast.success(tx('Cliente eliminado', 'Customer deleted'));
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting customer');
    }
  };

  // Writer functions
  const resetWriterForm = () => {
    setWriterForm({ name: '', email: '', active: true });
  };

  const openWriterDialog = (writer = null) => {
    if (writer) {
      setEditingWriter(writer);
      setWriterForm({
        name: writer.name || '',
        email: writer.email || '',
        active: writer.active !== false,
      });
    } else {
      setEditingWriter(null);
      resetWriterForm();
    }
    setWriterDialogOpen(true);
  };

  const handleSaveWriter = async () => {
    try {
      if (editingWriter) {
        await axios.put(`${API}/admin/writers/${editingWriter.id}`, writerForm);
        toast.success(tx('Redactor actualizado', 'Writer updated'));
      } else {
        await axios.post(`${API}/admin/writers`, writerForm);
        toast.success(tx('Redactor creado', 'Writer created'));
      }
      setWriterDialogOpen(false);
      fetchWriters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving writer');
    }
  };

  const handleDeleteWriter = async (writerId) => {
    if (!window.confirm(tx('¿Eliminar este redactor?', 'Delete this writer?'))) return;
    try {
      await axios.delete(`${API}/admin/writers/${writerId}`);
      toast.success(tx('Redactor eliminado', 'Writer deleted'));
      fetchWriters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting writer');
    }
  };

  // ==================== EXPENSE FUNCTIONS ====================
  
  const resetExpenseForm = () => {
    setExpenseForm({
      invoice_number: '',
      order_id: '',
      order_number: 0,
      order_pvp: 0,
      order_pc: 0,
      invoice_date: '',
      // Registration date — defaults to today, but the user can edit it
      // (useful when uploading old invoices retroactively).
      created_date: new Date().toISOString().split('T')[0],
      provider: '',
      base_amount: 0,
      iva: 0,
      irpf: 0,
      total: 0,
      payment_method: '',
      status: 'No pagada',
      notes: '',
    });
    setExpenseFiles([]);
    setPendingExpenseFiles([]);
  };

  const openExpenseDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setExpenseFiles(expense.attached_files || []);
      setPendingExpenseFiles([]);
      setExpenseForm({
        invoice_number: expense.invoice_number || '',
        order_id: expense.order_id || '',
        order_number: expense.order_number || 0,
        order_pvp: expense.order_pvp || 0,
        order_pc: expense.order_pc || 0,
        invoice_date: expense.invoice_date ? expense.invoice_date.split('T')[0] : '',
        created_date: expense.created_date
          ? expense.created_date.split('T')[0]
          : new Date().toISOString().split('T')[0],
        provider: expense.provider || '',
        base_amount: expense.base_amount || 0,
        iva: expense.iva || 0,
        irpf: expense.irpf || 0,
        total: expense.total || 0,
        payment_method: expense.payment_method || '',
        status: expense.status || 'No pagada',
        notes: expense.notes || '',
      });
    } else {
      setEditingExpense(null);
      resetExpenseForm();
    }
    setExpenseDialogOpen(true);
  };

  const viewOrderExpense = async (orderId) => {
    try {
      const response = await axios.get(`${API}/admin/expenses/by-order/${orderId}`);
      const expense = response.data;
      openExpenseDialog(expense);
      // Switch to expenses view to show context
      setActiveView('expenses');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t.noExpenseFound);
      } else {
        toast.error(error.response?.data?.detail || 'Error loading expense');
      }
    }
  };

  const handleOrderSelectForExpense = (order) => {
    setExpenseForm(prev => ({
      ...prev,
      order_id: order.id,
      order_number: order.order_number,
      order_pvp: order.pvp || 0,
      order_pc: order.pc || 0,
    }));
    setOrderSelectOpen(false);
  };

  const handleSaveExpense = async () => {
    // Validate required fields
    const errors = [];
    if (!expenseForm.invoice_number.trim()) {
      errors.push(t.invoiceNumberRequired);
    }
    if (!expenseForm.order_id) {
      errors.push(t.orderRequired);
    }
    if (!expenseForm.provider.trim()) {
      errors.push(t.providerRequired);
    }
    if (!expenseForm.base_amount || expenseForm.base_amount <= 0) {
      errors.push(t.baseAmountRequired);
    }
    if (!expenseForm.status) {
      errors.push(t.statusRequired);
    }
    if (!expenseForm.invoice_date) {
      errors.push(t.invoiceDateRequired);
    }
    if (!expenseForm.payment_method) {
      errors.push(t.paymentMethodRequired);
    }
    
    if (errors.length > 0) {
      toast.error(`${t.requiredFields}: ${errors.join(', ')}`);
      return;
    }
    
    try {
      const data = { ...expenseForm };
      if (data.invoice_date) {
        data.invoice_date = new Date(data.invoice_date).toISOString();
      } else {
        delete data.invoice_date;
      }

      if (editingExpense) {
        await axios.put(`${API}/admin/expenses/${editingExpense.id}`, data);
        toast.success(tx('Gasto actualizado', 'Expense updated'));
      } else {
        const response = await axios.post(`${API}/admin/expenses`, data);
        const newExpenseId = response.data.id;
        
        // Upload pending files
        if (pendingExpenseFiles.length > 0 && newExpenseId) {
          for (const pf of pendingExpenseFiles) {
            const formData = new FormData();
            formData.append('file', pf.file);
            await axios.post(`${API}/admin/expenses/${newExpenseId}/upload`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
          toast.success(tx('Gasto creado con archivos', 'Expense created with files'));
        } else {
          toast.success(tx('Gasto creado', 'Expense created'));
        }
      }
      setExpenseDialogOpen(false);
      fetchExpenses();
      fetchExpenseStats();
      fetchOrders(); // Refresh orders to update payment status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm(tx('¿Eliminar este gasto?', 'Delete this expense?'))) return;
    try {
      await axios.delete(`${API}/admin/expenses/${expenseId}`);
      toast.success(tx('Gasto eliminado', 'Expense deleted'));
      fetchExpenses();
      fetchExpenseStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting expense');
    }
  };

  // Expense file handlers
  const handleExpenseFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const allowedExtensions = ['.doc', '.docx', '.pdf', '.txt', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      toast.error(tx('Tipo de archivo no permitido', 'File type not allowed'));
      if (expenseFileInputRef.current) expenseFileInputRef.current.value = '';
      return;
    }
    
    if (editingExpense) {
      handleExpenseFileUpload(file, editingExpense.id);
    } else {
      setPendingExpenseFiles(prev => [...prev, {
        file: file,
        original_name: file.name,
        size: file.size,
        extension: fileExt
      }]);
      toast.success(tx('Archivo añadido', 'File added'));
    }
    
    if (expenseFileInputRef.current) expenseFileInputRef.current.value = '';
  };

  const handleExpenseFileUpload = async (file, expenseId) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/expenses/${expenseId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(tx('Archivo subido', 'File uploaded'));
      setExpenseFiles(prev => [...prev, response.data.file]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading file');
    }
  };

  const handleDownloadExpenseFile = async (expenseId, filename, originalName) => {
    try {
      const response = await axios.get(`${API}/admin/expenses/${expenseId}/files/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(tx('Error descargando archivo', 'Error downloading file'));
    }
  };

  const handleDeleteExpenseFile = async (expenseId, filename) => {
    if (!window.confirm(tx('¿Eliminar archivo?', 'Delete file?'))) return;
    try {
      await axios.delete(`${API}/admin/expenses/${expenseId}/files/${filename}`);
      toast.success(tx('Archivo eliminado', 'File deleted'));
      setExpenseFiles(prev => prev.filter(f => f.filename !== filename));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting file');
    }
  };

  const handleRemovePendingExpenseFile = (index) => {
    setPendingExpenseFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Download all expenses
  const handleDownloadExpenses = async () => {
    try {
      const params = {};
      
      if (expenseDownloadPreset === 'last_month') {
        params.preset = 'last_month';
      } else {
        if (expenseDownloadDateFrom) params.date_from = expenseDownloadDateFrom;
        if (expenseDownloadDateTo) params.date_to = expenseDownloadDateTo;
      }
      
      const response = await axios.get(`${API}/admin/expenses/download`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gastos_${expenseDownloadDateFrom || 'all'}_${expenseDownloadDateTo || 'all'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowExpenseDownloadDialog(false);
      toast.success(tx('Descarga iniciada', 'Download started'));
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(tx('Error al descargar', 'Download error'));
    }
  };

  // Income invoices functions
  const fetchIncomeInvoices = async () => {
    try {
      const response = await axios.get(`${API}/admin/income-invoices`, {
        params: { limit: 100 }
      });
      setIncomeInvoices(response.data.invoices);
    } catch (error) {
      console.error('Error fetching income invoices:', error);
    }
  };

  const fetchAllCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        params: { limit: 500 }
      });
      console.log('Customers loaded:', response.data.customers?.length || 0);
      setAllCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleIncomeCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setIncomeForm(prev => ({
        ...prev,
        client_name: customer.company || customer.name,
        client_nif: customer.nif || ''
      }));
    }
  };

  const openIncomeDialog = (income = null) => {
    // Fetch customers when opening dialog
    fetchAllCustomers();
    
    if (income) {
      setEditingIncome(income);
      setIncomeFiles(income.attached_files || []);
      setPendingIncomeFiles([]);
      setIncomeForm({
        invoice_number: income.invoice_number || '',
        order_number: income.order_number || 0,
        client_name: income.client_name || '',
        client_nif: income.client_nif || '',
        invoice_date: income.invoice_date ? income.invoice_date.split('T')[0] : '',
        base_amount: income.base_amount || 0,
        iva: income.iva || 21,
        irpf: income.irpf || 0,
        total: income.total || 0,
        payment_method: income.payment_method || '',
        status: income.status || 'A la espera de cobro',
        description: income.description || '',
        notes: income.notes || ''
      });
    } else {
      setEditingIncome(null);
      setIncomeFiles([]);
      setPendingIncomeFiles([]);
      setIncomeForm({
        invoice_number: '',
        order_number: 0,
        client_name: '',
        client_nif: '',
        invoice_date: new Date().toISOString().split('T')[0],
        base_amount: 0,
        iva: 21,
        irpf: 0,
        total: 0,
        payment_method: '',
        status: 'A la espera de cobro',
        description: '',
        notes: ''
      });
    }
    setShowIncomeDialog(true);
  };

  const handleSaveIncome = async () => {
    try {
      const data = {
        ...incomeForm,
        invoice_date: incomeForm.invoice_date ? new Date(incomeForm.invoice_date).toISOString() : null
      };
      
      let incomeId;
      if (editingIncome) {
        await axios.put(`${API}/admin/income-invoices/${editingIncome.id}`, data);
        incomeId = editingIncome.id;
        toast.success(tx('Factura actualizada', 'Invoice updated'));
      } else {
        const response = await axios.post(`${API}/admin/income-invoices`, data);
        incomeId = response.data.id;
        toast.success(tx('Factura creada', 'Invoice created'));
      }
      
      // Upload pending files
      for (const file of pendingIncomeFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/admin/income-invoices/${incomeId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowIncomeDialog(false);
      setPendingIncomeFiles([]);
      fetchIncomeInvoices();
    } catch (error) {
      console.error('Error saving income:', error);
      toast.error(tx('Error al guardar', 'Error saving'));
    }
  };

  const handleIncomeFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setPendingIncomeFiles(prev => [...prev, ...files]);
    if (incomeFileInputRef.current) incomeFileInputRef.current.value = '';
  };

  const handleRemovePendingIncomeFile = (index) => {
    setPendingIncomeFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteIncomeFile = async (incomeId, filename) => {
    if (!window.confirm(tx('¿Eliminar archivo?', 'Delete file?'))) return;
    try {
      await axios.delete(`${API}/admin/income-invoices/${incomeId}/files/${filename}`);
      toast.success(tx('Archivo eliminado', 'File deleted'));
      setIncomeFiles(prev => prev.filter(f => f.filename !== filename));
    } catch (error) {
      toast.error(tx('Error al eliminar archivo', 'Error deleting file'));
    }
  };

  const handleDownloadIncomeFile = async (incomeId, filename, originalName) => {
    try {
      const response = await axios.get(`${API}/admin/income-invoices/${incomeId}/files/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName || filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(tx('Error al descargar', 'Download error'));
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm(tx('¿Eliminar factura?', 'Delete invoice?'))) return;
    try {
      await axios.delete(`${API}/admin/income-invoices/${id}`);
      toast.success(tx('Factura eliminada', 'Invoice deleted'));
      fetchIncomeInvoices();
    } catch (error) {
      console.error('Error deleting income:', error);
      toast.error(tx('Error al eliminar', 'Error deleting'));
    }
  };

  const handleDownloadIncome = async () => {
    try {
      const params = {};
      if (incomeDownloadPreset === 'last_month') {
        params.preset = 'last_month';
      } else {
        if (incomeDownloadDateFrom) params.date_from = incomeDownloadDateFrom;
        if (incomeDownloadDateTo) params.date_to = incomeDownloadDateTo;
      }
      
      const response = await axios.get(`${API}/admin/income-invoices/download`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ingresos_${incomeDownloadDateFrom || 'all'}_${incomeDownloadDateTo || 'all'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowIncomeDownloadDialog(false);
      toast.success(tx('Descarga iniciada', 'Download started'));
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(tx('Error al descargar', 'Download error'));
    }
  };

  // Download single invoice as PDF
  const handleDownloadSingleInvoice = async (invoiceId, invoiceNumber) => {
    try {
      toast.loading(tx('Generando PDF...', 'Generating PDF...'), { id: 'download-single' });
      
      const response = await axios.get(`${API}/admin/invoices/${invoiceId}/download-pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${invoiceNumber || 'factura'}.pdf`;
      link.click();
      
      toast.success(tx('PDF descargado', 'PDF downloaded'), { id: 'download-single' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error downloading PDF', { id: 'download-single' });
    }
  };

  // Handle select/deselect all invoices
  const handleSelectAllInvoices = (checked) => {
    if (checked) {
      setSelectedInvoices(incomeInvoices.map(inv => inv.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  // Handle single invoice selection
  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    } else {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    }
  };

  // Download selected invoices as ZIP
  const handleDownloadSelectedInvoices = async () => {
    if (selectedInvoices.length === 0) {
      toast.error(tx('Selecciona al menos una factura', 'Select at least one invoice'));
      return;
    }
    
    try {
      toast.loading(tx('Generando archivo...', 'Generating file...'), { id: 'download-invoices' });
      
      const response = await axios.post(`${API}/admin/invoices/download-multiple`, 
        { invoice_ids: selectedInvoices },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `facturas_${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
      
      toast.success(tx('Facturas descargadas', 'Invoices downloaded'), { id: 'download-invoices' });
      setSelectedInvoices([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error downloading invoices', { id: 'download-invoices' });
    }
  };

  // Calculate income total when amounts change
  React.useEffect(() => {
    const base = parseFloat(incomeForm.base_amount) || 0;
    const ivaAmount = base * (parseFloat(incomeForm.iva) || 0) / 100;
    const irpfAmount = base * (parseFloat(incomeForm.irpf) || 0) / 100;
    const total = base + ivaAmount - irpfAmount;
    setIncomeForm(prev => ({ ...prev, total: Math.round(total * 100) / 100 }));
  }, [incomeForm.base_amount, incomeForm.iva, incomeForm.irpf]);

  // Fetch income invoices on mount
  React.useEffect(() => {
    fetchIncomeInvoices();
    fetchAllCustomers();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Tabs - Modern pill style */}
      <div className="flex flex-wrap gap-2 p-1 bg-slate-100 rounded-xl w-fit">
        <Button
          variant={activeView === 'orders' ? 'default' : 'ghost'}
          onClick={() => setActiveView('orders')}
          className={activeView === 'orders' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md' : 'text-slate-600 hover:text-indigo-600 hover:bg-white'}
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          {t.orders}
        </Button>
        <Button
          variant={activeView === 'customers' ? 'default' : 'ghost'}
          onClick={() => setActiveView('customers')}
          className={activeView === 'customers' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md' : 'text-slate-600 hover:text-emerald-600 hover:bg-white'}
        >
          <Users className="h-4 w-4 mr-2" />
          {t.customers}
        </Button>
        <Button
          variant={activeView === 'writers' ? 'default' : 'ghost'}
          onClick={() => setActiveView('writers')}
          className={activeView === 'writers' ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md' : 'text-slate-600 hover:text-amber-600 hover:bg-white'}
        >
          <Pencil className="h-4 w-4 mr-2" />
          {t.writers}
        </Button>
        <Button
          variant={activeView === 'expenses' ? 'default' : 'ghost'}
          onClick={() => setActiveView('expenses')}
          className={activeView === 'expenses' ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-md' : 'text-slate-600 hover:text-rose-600 hover:bg-white'}
        >
          <FileText className="h-4 w-4 mr-2" />
          {t.expenses}
        </Button>
        <Button
          variant={activeView === 'stats' ? 'default' : 'ghost'}
          onClick={() => setActiveView('stats')}
          className={activeView === 'stats' ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md' : 'text-slate-600 hover:text-violet-600 hover:bg-white'}
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          {t.stats}
        </Button>
        <Button
          variant={activeView === 'accounting' ? 'default' : 'ghost'}
          onClick={() => setActiveView('accounting')}
          className={activeView === 'accounting' ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md' : 'text-slate-600 hover:text-blue-600 hover:bg-white'}
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          {tx('Contabilidad', 'Accounting')}
        </Button>
      </div>

      {/* Stats View */}
      {activeView === 'stats' && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-indigo-500 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CardDescription className="text-slate-500">{t.totalOrders}</CardDescription>
              <CardTitle className="text-3xl font-bold text-indigo-600">{stats.total_orders}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-l-4 border-l-emerald-500 bg-emerald-50/50 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CardDescription className="text-slate-500">{t.totalRevenue}</CardDescription>
              <CardTitle className="text-3xl font-bold text-emerald-600">€{stats.total_pvp?.toLocaleString()}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-l-4 border-l-rose-500 bg-rose-50/50 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CardDescription className="text-slate-500">{t.totalCost}</CardDescription>
              <CardTitle className="text-3xl font-bold text-rose-600">€{stats.total_pc?.toLocaleString()}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-l-4 border-l-sky-500 bg-sky-50/50 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CardDescription className="text-slate-500">{t.margin}</CardDescription>
              <CardTitle className="text-3xl font-bold text-sky-600">€{stats.margin?.toLocaleString()}</CardTitle>
            </CardHeader>
          </Card>
          
          {/* Status breakdown */}
          <Card className="col-span-2 md:col-span-4 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg text-slate-700">{t.status}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {ORDER_STATUSES.map(status => (
                  <Badge key={status} className={STATUS_COLORS[status]}>
                    {status}: {stats.status_counts?.[status] || 0}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Accounting Dashboard View */}
      {activeView === 'accounting' && (
        <AccountingDashboard language={language} />
      )}

      {/* Orders View */}
      {activeView === 'orders' && (
        <>
          {/* Filters */}
          <Card className="p-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t.search}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t.allStatuses} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allStatuses}</SelectItem>
                  {ORDER_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={paymentFilter} onValueChange={(v) => { setPaymentFilter(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t.allPayments} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allPayments}</SelectItem>
                  {PAYMENT_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={orderTypeFilter} onValueChange={(v) => { setOrderTypeFilter(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder={tx('Tipo Pedido', 'Order Type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{tx('Todos los tipos', 'All types')}</SelectItem>
                  <SelectItem value="P">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      P# (Internos)
                    </span>
                  </SelectItem>
                  <SelectItem value="E">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                      E# (Editores)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit"><Search className="h-4 w-4" /></Button>
              <Button type="button" variant="outline" onClick={() => { setSearch(''); setStatusFilter(''); setPaymentFilter(''); setOrderTypeFilter(''); fetchOrders(1); }} className="hover:bg-slate-100">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </form>
          </Card>

          {/* Actions */}
          <div className="flex justify-between items-center">
            <p className="text-slate-500 font-medium">
              {pagination.total} {t.orders}
            </p>
            <div className="flex gap-2">
              {/* Hidden file input for import */}
              <input
                type="file"
                ref={ordersImportInputRef}
                accept=".xlsx,.xls"
                onChange={handleImportOrders}
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={handleDownloadOrdersTemplate}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                {t.downloadTemplate}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => ordersImportInputRef.current?.click()}
                disabled={importingOrders}
                className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400"
              >
                {importingOrders ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {t.import}
              </Button>
              <Button variant="outline" onClick={handleExportOrders} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400">
                <Download className="h-4 w-4 mr-2" />
                {t.export}
              </Button>
              {selectedOrders.length > 0 && (
                <Button 
                  variant="destructive" 
                  onClick={handleBulkDeleteOrders}
                  disabled={bulkDeleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {bulkDeleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Eliminar ({selectedOrders.length})
                </Button>
              )}
              <Button onClick={() => openOrderDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md">
                <Plus className="h-4 w-4 mr-2" />
                {t.newOrder}
              </Button>
            </div>
          </div>

          {/* Orders Table */}
          <Card className="shadow-md border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="px-1 w-[40px]">
                      <Checkbox 
                        checked={orders.length > 0 && selectedOrders.length === orders.length}
                        onCheckedChange={handleSelectAllOrders}
                        data-testid="select-all-orders"
                      />
                    </TableHead>
                    <SortHeader field="order_number" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>{t.orderNumber}</SortHeader>
                    <TableHead className="px-1">{tx('Nº Ext', 'Ext#')}</TableHead>
                    <SortHeader field="customer_name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>{t.customer}</SortHeader>
                    <TableHead className="px-1">{tx('Medio', 'Media')}</TableHead>
                    <TableHead className="px-1">{t.status}</TableHead>
                    <TableHead className="px-1">{tx('Pago', 'Pay')}</TableHead>
                    <SortHeader field="pvp" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>{t.pvp}</SortHeader>
                    <TableHead className="px-1">{tx('PVP.S', 'PVP.S')}</TableHead>
                    <TableHead className="px-1">{t.pc}</TableHead>
                    <TableHead className="px-1">{tx('PC.S', 'PC.S')}</TableHead>
                    <TableHead className="px-1">{tx('Redactor', 'Writer')}</TableHead>
                    <TableHead className="px-1">Email</TableHead>
                    <TableHead className="px-1">{tx('URL Pub.', 'Pub. URL')}</TableHead>
                    <TableHead className="px-1 text-center">{tx('Fact.', 'Inv.')}</TableHead>
                    <TableHead className="px-1 text-center">{tx('Est.G', 'Exp.St')}</TableHead>
                    <SortHeader field="order_date" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>{tx('Fecha', 'Date')}</SortHeader>
                    <TableHead className="text-right px-1">{t.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={19} className="text-center py-8 text-muted-foreground">
                        {t.noOrders}
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} className={`hover:bg-slate-50/50 ${selectedOrders.includes(order.id) ? 'bg-blue-50' : ''}`}>
                        <TableCell className="px-1">
                          <Checkbox 
                            checked={selectedOrders.includes(order.id)}
                            onCheckedChange={(checked) => handleSelectOrder(order.id, checked)}
                            data-testid={`select-order-${order.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-bold text-indigo-600 px-1 whitespace-nowrap">
                          {order.legacy_order_id || `#${order.order_number}`}
                          {order.order_type && (
                            <span className={`ml-1 text-[10px] px-1 py-0.5 rounded ${
                              order.order_type === 'E' ? 'bg-purple-100 text-purple-700' : 
                              order.order_type === 'P' ? 'bg-blue-100 text-blue-700' : 
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {order.order_type === 'E' ? 'Ed' : order.order_type === 'P' ? 'Int' : 'Ext'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground px-1 text-[11px]">{order.external_order_number || '-'}</TableCell>
                        <TableCell className="px-1 max-w-[100px]">
                          <div className="truncate font-medium text-[11px]" title={order.customer_name}>{order.customer_name}</div>
                          <div className="truncate text-[10px] text-muted-foreground" title={order.customer_email}>{order.customer_email}</div>
                        </TableCell>
                        <TableCell className="max-w-[120px] px-1">
                          <div className="truncate font-medium text-[11px]" title={order.product_url || order.editor_name}>
                            {order.product_url || order.editor_name || '-'}
                          </div>
                          {/* Show product owner email or editor email */}
                          {order.order_type === 'E' && order.editor_email ? (
                            <div className="text-[10px] text-muted-foreground truncate">{order.editor_email}</div>
                          ) : order.product_owner_email ? (
                            <div className="text-[10px] text-blue-600 truncate" title={order.product_owner_email}>
                              {order.product_owner_email}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="px-1">
                          <div className="flex items-center gap-1">
                            <Select 
                              value={order.status} 
                              onValueChange={(v) => handleStatusChange(order.id, v, order)}
                            >
                              <SelectTrigger className={`w-[110px] h-7 text-[10px] ${STATUS_COLORS[order.status]}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ORDER_STATUSES.map(s => (
                                  <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Edit button next to status */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50"
                              title={tx('Editar pedido', 'Edit order')}
                              onClick={() => openOrderDialog(order)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            {/* Button to show template when status is Medio */}
                            {order.status === 'Medio' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                title={tx('Ver plantilla de email', 'View email template')}
                                onClick={() => {
                                  setMedioPopupOrder(order);
                                  setMedioPopupLanguage('es');
                                  setMedioCopied(false);
                                  setShowMedioPopup(true);
                                }}
                              >
                                <Mail className="h-3 w-3 text-blue-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-1">
                          <div className="flex items-center gap-1">
                            <Badge className={`${PAYMENT_COLORS[order.payment_status]} text-[10px] px-1`}>
                              {order.payment_status?.substring(0, 6) || '-'}
                            </Badge>
                            {order.payment_status === 'Pagado' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => viewOrderExpense(order.id)}
                                title={t.viewExpense}
                                className="h-5 w-5 p-0 text-primary hover:text-primary/80"
                                data-testid={`view-expense-${order.order_number}`}
                              >
                                <Receipt className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-1 text-[11px] text-green-600 font-medium">€{order.pvp || 0}</TableCell>
                        <TableCell className="px-1 text-[11px] text-orange-600 font-medium">€{order.pvp_sensitive || 0}</TableCell>
                        <TableCell className="px-1 text-[11px] text-red-600">€{order.pc || 0}</TableCell>
                        <TableCell className="px-1 text-[11px] text-purple-600">€{order.pc_sensitive || order.premium_pc || 0}</TableCell>
                        <TableCell className="px-1 text-[11px] max-w-[60px] truncate" title={order.order_type === 'E' ? order.editor_name : order.writer}>
                          {order.order_type === 'E' 
                            ? (order.editor_name || '-')
                            : (order.writer || '-')
                          }
                        </TableCell>
                        <TableCell className="px-1 text-[10px] text-muted-foreground max-w-[80px] truncate" title={order.order_type === 'E' ? order.editor_email : order.writer_email}>
                          {order.order_type === 'E'
                            ? (order.editor_email || '-')
                            : (order.writer_email || '-')
                          }
                        </TableCell>
                        <TableCell className="px-1 text-[10px] max-w-[100px]">
                          {order.published_url ? (
                            <a 
                              href={order.published_url.startsWith('http') ? order.published_url : `https://${order.published_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline truncate block"
                              title={order.published_url}
                            >
                              {order.published_url.length > 25 ? order.published_url.substring(0, 25) + '...' : order.published_url}
                            </a>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center px-1">
                          {order.expense_id ? (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => viewOrderExpense(order.id)}
                              title={tx('Ver factura de gasto vinculada', 'View linked expense invoice')}
                              className="h-5 w-5 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`view-invoice-${order.order_number}`}
                            >
                              <FileText className="h-3 w-3" />
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center px-1">
                          {order.expense_status ? (
                            <Badge className={`text-[9px] px-1 ${
                              order.expense_status === 'Pagada' ? 'bg-green-100 text-green-700' :
                              order.expense_status === 'No pagada' ? 'bg-red-100 text-red-700' :
                              order.expense_status === 'Pagada 50%' ? 'bg-orange-100 text-orange-700' :
                              order.expense_status === 'Con proforma' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {order.expense_status?.substring(0, 8)}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">-</span>
                          )}
                        </TableCell>
                        <TableCell className="px-1 text-[11px] whitespace-nowrap">{order.order_date ? new Date(order.order_date).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit'}) : '-'}</TableCell>
                        <TableCell className="text-right px-1">
                          <div className="flex items-center justify-end gap-0">
                            <Button variant="ghost" size="sm" onClick={() => openOrderDialog(order)} className="h-6 w-6 p-0 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50">
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteOrder(order.id)} className="h-6 w-6 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" size="sm" onClick={() => fetchOrders(pagination.page - 1)} disabled={!pagination.has_prev}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">{t.page} {pagination.page} {t.of} {pagination.total_pages}</span>
              <Button variant="outline" size="sm" onClick={() => fetchOrders(pagination.page + 1)} disabled={!pagination.has_next}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Customers View */}
      {activeView === 'customers' && (
        <>
          <div className="flex justify-between items-center">
            <p className="text-slate-500 font-medium">{customers.length} {t.customers}</p>
            <Button onClick={() => openCustomerDialog()} className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md">
              <Plus className="h-4 w-4 mr-2" />
              {t.newCustomer}
            </Button>
          </div>

          <Card className="shadow-md border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t.customerNumber}</TableHead>
                    <TableHead>{t.name}</TableHead>
                    <TableHead>{t.email}</TableHead>
                    <TableHead>{t.phone}</TableHead>
                    <TableHead>{t.company}</TableHead>
                    <TableHead>CIF/NIF</TableHead>
                    <TableHead className="text-right">{t.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        {t.noCustomers}
                      </TableCell>
                    </TableRow>
                  ) : (
                    customers.map((customer) => (
                      <TableRow key={customer.id} className="hover:bg-slate-50/50">
                        <TableCell className="font-bold text-emerald-600">#{customer.customer_number}</TableCell>
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell>{customer.email}</TableCell>
                        <TableCell>{customer.phone}</TableCell>
                        <TableCell>{customer.company}</TableCell>
                        <TableCell>{customer.nif || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openCustomerDialog(customer)} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteCustomer(customer.id)} className="text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Writers View */}
      {activeView === 'writers' && (
        <>
          <div className="flex justify-between items-center">
            <p className="text-slate-500 font-medium">{writers.length} {t.writers}</p>
            <Button onClick={() => openWriterDialog()} className="bg-amber-600 hover:bg-amber-700 text-white shadow-md">
              <Plus className="h-4 w-4 mr-2" />
              {t.newWriter}
            </Button>
          </div>

          <Card className="shadow-md border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t.name}</TableHead>
                    <TableHead>{t.email}</TableHead>
                    <TableHead>{t.active}</TableHead>
                    <TableHead className="text-right">{t.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {writers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        {t.noWriters}
                      </TableCell>
                    </TableRow>
                  ) : (
                    writers.map((writer) => (
                      <TableRow key={writer.id} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium">{writer.name}</TableCell>
                        <TableCell>{writer.email}</TableCell>
                        <TableCell>
                          <Badge className={writer.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500'}>
                            {writer.active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openWriterDialog(writer)} className="text-slate-600 hover:text-amber-600 hover:bg-amber-50">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteWriter(writer.id)} className="text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* EXPENSES VIEW */}
      {activeView === 'expenses' && (
        <>
          {/* Expense Stats */}
          {expenseStats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <Card className="border-l-4 border-l-rose-500 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardDescription className="text-slate-500">{t.totalExpenses}</CardDescription>
                  <CardTitle className="text-2xl font-bold text-rose-600">{expenseStats.total_expenses}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-l-4 border-l-amber-500 bg-amber-50/50 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardDescription className="text-slate-500">{t.totalBase}</CardDescription>
                  <CardTitle className="text-2xl font-bold text-amber-600">€{expenseStats.total_base?.toLocaleString()}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-l-4 border-l-red-500 bg-red-50/50 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardDescription className="text-slate-500">{t.total}</CardDescription>
                  <CardTitle className="text-2xl font-bold text-red-600">€{expenseStats.total_amount?.toLocaleString()}</CardTitle>
                </CardHeader>
              </Card>
            </div>
          )}

          {/* Expense Filters and Add Button */}
          <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-2">
              <Input
                placeholder={t.search}
                value={expenseSearch}
                onChange={(e) => setExpenseSearch(e.target.value)}
                className="w-48"
              />
              <Select value={expensePaymentFilter} onValueChange={setExpensePaymentFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={t.paymentMethod} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{tx('Todas', 'All')}</SelectItem>
                  {PAYMENT_METHODS.map(pm => (
                    <SelectItem key={pm} value={pm}>{pm}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => fetchExpenses()} className="hover:bg-slate-100">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowExpenseDownloadDialog(true)}>
                <Download className="h-4 w-4 mr-2" />
                {tx('Descargar', 'Download')}
              </Button>
              {/* Import Expenses */}
              <input
                type="file"
                ref={expensesImportInputRef}
                onChange={handleImportExpenses}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <Button variant="outline" onClick={handleDownloadExpensesTemplate}>
                <Download className="h-4 w-4 mr-2" />
                {t.downloadTemplate}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => expensesImportInputRef.current?.click()}
                disabled={importingExpenses}
              >
                {importingExpenses ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {t.importExpenses}
              </Button>
              <Button onClick={() => openExpenseDialog()} data-testid="add-expense-btn" className="bg-rose-600 hover:bg-rose-700 text-white shadow-md">
                <Plus className="h-4 w-4 mr-2" />
                {t.newExpense}
              </Button>
            </div>
          </div>

          {/* Expenses Table */}
          <Card className="shadow-md border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t.invoiceNumber}</TableHead>
                    <TableHead>{t.orderNumber}</TableHead>
                    <TableHead>{t.provider}</TableHead>
                    <TableHead>{t.baseAmount}</TableHead>
                    <TableHead>{t.iva}</TableHead>
                    <TableHead>{t.irpf}</TableHead>
                    <TableHead>{t.total}</TableHead>
                    <TableHead>{t.expenseStatus}</TableHead>
                    <TableHead>{t.paymentMethod}</TableHead>
                    <TableHead>{t.expenseInvoiceDate}</TableHead>
                    <TableHead>{t.createdDate}</TableHead>
                    <TableHead className="text-right">{t.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                        {t.noExpenses}
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell className="font-medium">{expense.invoice_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={expense.order_id === 'S/N' || !expense.order_number ? 'bg-amber-50 text-amber-700 border-amber-300' : ''}>
                            {expense.order_id === 'S/N' || !expense.order_number ? 'S/N' : `#${expense.order_number}`}
                          </Badge>
                        </TableCell>
                        <TableCell>{expense.provider}</TableCell>
                        <TableCell>€{expense.base_amount?.toFixed(2)}</TableCell>
                        <TableCell>€{expense.iva?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>€{expense.irpf?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell className="font-semibold text-red-600">€{expense.total?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className={
                            expense.status === 'Pagada' ? 'bg-green-500 hover:bg-green-600' :
                            expense.status === 'Pagada 50%' ? 'bg-yellow-500 hover:bg-yellow-600' :
                            expense.status === 'Con proforma' ? 'bg-blue-500 hover:bg-blue-600' :
                            'bg-red-500 hover:bg-red-600'
                          }>
                            {expense.status || 'No pagada'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{expense.payment_method}</Badge>
                        </TableCell>
                        <TableCell>
                          {expense.invoice_date ? new Date(expense.invoice_date).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {expense.created_date ? new Date(expense.created_date).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openExpenseDialog(expense)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteExpense(expense.id)} className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Expense Pagination */}
          {expensePagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={expensePagination.page <= 1}
                onClick={() => fetchExpenses(expensePagination.page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                {t.page} {expensePagination.page} {t.of} {expensePagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={expensePagination.page >= expensePagination.total_pages}
                onClick={() => fetchExpenses(expensePagination.page + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* ===== INCOME INVOICES SECTION ===== */}
          <div className="mt-12 pt-8 border-t-2 border-green-200">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-4">
              <h3 className="text-xl font-bold text-green-700 flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                {tx('Facturas de Ingresos', 'Income Invoices')}
                {selectedInvoices.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedInvoices.length} {tx('seleccionadas', 'selected')}
                  </Badge>
                )}
              </h3>
              <div className="flex gap-2">
                {selectedInvoices.length > 0 && (
                  <Button variant="default" onClick={handleDownloadSelectedInvoices} className="bg-blue-600 hover:bg-blue-700">
                    <Download className="h-4 w-4 mr-2" />
                    {tx(`Descargar (${selectedInvoices.length})`, `Download (${selectedInvoices.length})`)}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowIncomeDownloadDialog(true)}>
                  <Download className="h-4 w-4 mr-2" />
                  {tx('Descargar por Fecha', 'Download by Date')}
                </Button>
                {/* Import Income */}
                <input
                  type="file"
                  ref={incomeImportInputRef}
                  onChange={handleImportIncome}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                <Button variant="outline" onClick={handleDownloadIncomeTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  {t.downloadTemplate}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => incomeImportInputRef.current?.click()}
                  disabled={importingIncome}
                >
                  {importingIncome ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {t.importIncome}
                </Button>
                <Button onClick={() => openIncomeDialog()} className="bg-green-600 hover:bg-green-700 text-white shadow-md">
                  <Plus className="h-4 w-4 mr-2" />
                  {tx('Añadir Ingreso', 'Add Income')}
                </Button>
              </div>
            </div>

            {/* Income Table */}
            <Card className="shadow-md border-green-200">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-green-50">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={incomeInvoices.length > 0 && selectedInvoices.length === incomeInvoices.length}
                          onCheckedChange={handleSelectAllInvoices}
                          aria-label="Select all"
                        />
                      </TableHead>
                      <TableHead>{t.invoiceNumber}</TableHead>
                      <TableHead>{tx('Cliente', 'Client')}</TableHead>
                      <TableHead>{tx('NIF/CIF', 'Tax ID')}</TableHead>
                      <TableHead>{t.baseAmount}</TableHead>
                      <TableHead>{t.total}</TableHead>
                      <TableHead>{tx('Estado', 'Status')}</TableHead>
                      <TableHead>{tx('Fecha', 'Date')}</TableHead>
                      <TableHead className="text-right">{t.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {incomeInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          {tx('No hay facturas de ingresos', 'No income invoices')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      incomeInvoices.map((inv) => {
                        const isRecharge = inv.source_type === 'recharge';
                        return (
                          <TableRow key={inv.id} className={`hover:bg-green-50/50 ${selectedInvoices.includes(inv.id) ? 'bg-blue-50' : ''}`}>
                            <TableCell>
                              <Checkbox
                                checked={selectedInvoices.includes(inv.id)}
                                onCheckedChange={(checked) => handleSelectInvoice(inv.id, checked)}
                                aria-label={`Select invoice ${inv.invoice_number}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {inv.invoice_number || '-'}
                                {isRecharge && (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    Recarga
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{inv.client_name || '-'}</TableCell>
                            <TableCell>{inv.client_nif || '-'}</TableCell>
                            <TableCell>€{inv.base_amount?.toLocaleString() || 0}</TableCell>
                            <TableCell className="font-bold text-green-600">€{inv.total?.toLocaleString() || 0}</TableCell>
                            <TableCell>
                              <Badge className={INCOME_STATUS_COLORS[inv.status] || 'bg-gray-100'}>
                                {inv.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{inv.invoice_date ? inv.invoice_date.split('T')[0] : '-'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDownloadSingleInvoice(inv.id, inv.invoice_number)}
                                  title={tx('Descargar PDF', 'Download PDF')}
                                >
                                  <Download className="h-4 w-4 text-blue-500" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openIncomeDialog(inv)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                {/* Don't allow deleting recharge invoices */}
                                {!isRecharge && (
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteIncome(inv.id)}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingExpense ? t.editExpense : t.newExpense}</DialogTitle>
            <DialogDescription className="text-xs text-red-500">* {t.requiredFields}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Nº Factura */}
            <div>
              <Label className="text-red-600">{t.invoiceNumber} *</Label>
              <Input 
                value={expenseForm.invoice_number}
                onChange={(e) => setExpenseForm({...expenseForm, invoice_number: e.target.value})}
                placeholder="Ej: F-2024-001"
                data-testid="expense-invoice-number"
                className={!expenseForm.invoice_number.trim() ? 'border-red-300' : ''}
              />
            </div>
            
            {/* Nº Pedido - Combobox */}
            <div>
              <Label className="text-red-600">{t.orderNumber} *</Label>
              <Popover open={orderSelectOpen} onOpenChange={setOrderSelectOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={`w-full justify-between font-normal ${!expenseForm.order_id ? 'border-red-300' : ''}`}
                    data-testid="expense-order-select"
                  >
                    {expenseForm.order_id === 'S/N' ? 'S/N' : (expenseForm.order_number ? `#${expenseForm.order_number}` : t.selectOrder)}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t.selectOrder} />
                    <CommandList>
                      <CommandEmpty>{t.noOrderFound}</CommandEmpty>
                      <CommandGroup heading={tx('Opciones', 'Options')}>
                        {/* S/N Option - Always visible */}
                        <CommandItem
                          key="sn"
                          value="sn sin numero pedido"
                          onSelect={() => {
                            setExpenseForm({
                              ...expenseForm,
                              order_id: 'S/N',
                              order_number: 0,
                              order_pvp: 0,
                              order_pc: 0,
                            });
                            setOrderSelectOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              expenseForm.order_id === 'S/N' ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="font-medium text-amber-600">S/N - {tx('Sin Número de Pedido', 'No Order Number')}</span>
                        </CommandItem>
                      </CommandGroup>
                      <CommandGroup heading={tx('Pedidos', 'Orders')}>
                        {orders.map((order) => (
                          <CommandItem
                            key={order.id}
                            value={`${order.order_number} ${order.product_url}`}
                            onSelect={() => handleOrderSelectForExpense(order)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                expenseForm.order_id === order.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">#{order.order_number} - {order.product_url}</span>
                              <span className="text-xs text-muted-foreground">
                                PVP: €{order.pvp} | PC: €{order.pc}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Order Prices Display */}
            {expenseForm.order_id && expenseForm.order_id !== 'S/N' && (
              <div className="col-span-2 p-3 bg-muted rounded-md">
                <div className="flex gap-8">
                  <div>
                    <span className="text-sm text-muted-foreground">{t.salePriceLabel} (PVP):</span>
                    <span className="ml-2 font-semibold text-green-600">€{expenseForm.order_pvp}</span>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">{t.purchasePriceLabel} (PC):</span>
                    <span className="ml-2 font-semibold text-red-600">€{expenseForm.order_pc}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Fecha Factura Gasto */}
            <div>
              <Label className="text-red-600">{t.expenseInvoiceDate} *</Label>
              <Input 
                type="date"
                value={expenseForm.invoice_date}
                onChange={(e) => setExpenseForm({...expenseForm, invoice_date: e.target.value})}
                className={!expenseForm.invoice_date ? 'border-red-300' : ''}
              />
            </div>
            
            {/* Fecha de Registro — por defecto hoy, pero editable
                (útil para registrar facturas antiguas retroactivamente) */}
            <div>
              <Label>{t.createdDate}</Label>
              <Input
                type="date"
                value={expenseForm.created_date || ''}
                onChange={(e) => setExpenseForm({...expenseForm, created_date: e.target.value})}
                data-testid="expense-created-date-input"
              />
            </div>
            
            {/* Proveedor */}
            <div>
              <Label className="text-red-600">{t.provider} *</Label>
              <Input 
                value={expenseForm.provider}
                onChange={(e) => setExpenseForm({...expenseForm, provider: e.target.value})}
                placeholder="Nombre del proveedor"
                className={!expenseForm.provider.trim() ? 'border-red-300' : ''}
              />
            </div>
            
            {/* PVP sin IVA */}
            <div>
              <Label className="text-red-600">{t.baseAmount} (€) *</Label>
              <Input 
                type="number"
                value={expenseForm.base_amount}
                onChange={(e) => {
                  const base = parseFloat(e.target.value) || 0;
                  const total = base + expenseForm.iva - expenseForm.irpf;
                  setExpenseForm({...expenseForm, base_amount: base, total: parseFloat(total.toFixed(2))});
                }}
                className={`bg-orange-50 ${!expenseForm.base_amount || expenseForm.base_amount <= 0 ? 'border-red-300' : ''}`}
              />
            </div>
            
            {/* IVA (valor en euros, manual) */}
            <div>
              <Label>{t.iva} (€)</Label>
              <Input 
                type="number"
                value={expenseForm.iva}
                onChange={(e) => {
                  const iva = parseFloat(e.target.value) || 0;
                  const total = expenseForm.base_amount + iva - expenseForm.irpf;
                  setExpenseForm({...expenseForm, iva: iva, total: parseFloat(total.toFixed(2))});
                }}
              />
            </div>
            
            {/* IRPF (valor en euros, manual) */}
            <div>
              <Label>{t.irpf} (€)</Label>
              <Input 
                type="number"
                value={expenseForm.irpf}
                onChange={(e) => {
                  const irpf = parseFloat(e.target.value) || 0;
                  const total = expenseForm.base_amount + expenseForm.iva - irpf;
                  setExpenseForm({...expenseForm, irpf: irpf, total: parseFloat(total.toFixed(2))});
                }}
              />
            </div>
            
            {/* Total (calculado automáticamente) */}
            <div>
              <Label>{t.total} (€)</Label>
              <Input 
                type="number"
                value={expenseForm.total}
                disabled
                className="bg-green-50 font-semibold cursor-not-allowed"
              />
            </div>
            
            {/* Estado */}
            <div>
              <Label className="text-red-600">{t.expenseStatus} *</Label>
              <Select value={expenseForm.status} onValueChange={(v) => setExpenseForm({...expenseForm, status: v})}>
                <SelectTrigger className={!expenseForm.status ? 'border-red-300' : ''}>
                  <SelectValue placeholder={t.expenseStatus} />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Forma de Pago */}
            <div>
              <Label className="text-red-600">{t.paymentMethod} *</Label>
              <Select value={expenseForm.payment_method} onValueChange={(v) => setExpenseForm({...expenseForm, payment_method: v})}>
                <SelectTrigger className={!expenseForm.payment_method ? 'border-red-300' : ''}>
                  <SelectValue placeholder={t.paymentMethod} />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(pm => (
                    <SelectItem key={pm} value={pm}>{pm}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Notas */}
            <div className="col-span-2">
              <Label>{t.notes}</Label>
              <Textarea 
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                rows={2}
              />
            </div>
            
            {/* Archivos */}
            <div className="col-span-2">
              <Label>{tx('Facturas Adjuntas', 'Attached Invoices')}</Label>
              <div className="mt-2 space-y-2">
                {/* Archivos existentes */}
                {editingExpense && expenseFiles.length > 0 && (
                  <div className="space-y-1">
                    {expenseFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm">{file.original_name}</span>
                          <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleDownloadExpenseFile(editingExpense.id, file.filename, file.original_name)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteExpenseFile(editingExpense.id, file.filename)} className="text-red-500">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Archivos pendientes */}
                {!editingExpense && pendingExpenseFiles.length > 0 && (
                  <div className="space-y-1">
                    {pendingExpenseFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm">{file.original_name}</span>
                          <Badge variant="outline" className="text-yellow-600">{tx('Pendiente', 'Pending')}</Badge>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemovePendingExpenseFile(index)} className="text-red-500">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Botón subir */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={expenseFileInputRef}
                    onChange={handleExpenseFileSelect}
                    accept=".doc,.docx,.pdf,.txt,.xls,.xlsx,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => expenseFileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {tx('Subir Factura', 'Upload Invoice')}
                  </Button>
                  <span className="text-xs text-muted-foreground">.pdf, .doc, .jpg, .png...</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveExpense} data-testid="save-expense-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOrder ? t.editOrder : t.newOrder}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* 1. Cliente */}
            <div>
              <Label>{t.customer}</Label>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="customer-combobox"
                  >
                    {orderForm.customer_name || t.selectCustomer}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t.selectCustomer} />
                    <CommandList>
                      <CommandEmpty>{t.noCustomerFound}</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            value={customer.name}
                            onSelect={() => handleCustomerSelect(customer)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                orderForm.customer_id === customer.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{customer.name}</span>
                              <span className="text-xs text-muted-foreground">{customer.email}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* 2. Email Cliente */}
            <div>
              <Label>{t.email} Cliente</Label>
              <Input 
                type="email" 
                value={orderForm.customer_email} 
                onChange={(e) => setOrderForm({...orderForm, customer_email: e.target.value})} 
                data-testid="customer-email-input"
              />
            </div>
            
            {/* 2.5 Nº P Exter */}
            <div>
              <Label>{t.externalOrderNumber}</Label>
              <Input 
                value={orderForm.external_order_number} 
                onChange={(e) => setOrderForm({...orderForm, external_order_number: e.target.value})}
                placeholder="Ej: EXT-2024-001"
                data-testid="external-order-number-input"
              />
            </div>
            
            {/* 3. Producto */}
            <div>
              <Label>{t.product}</Label>
              <Popover open={productOpen} onOpenChange={setProductOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={productOpen}
                    className="w-full justify-between font-normal"
                    data-testid="product-combobox"
                  >
                    {orderForm.product_url || t.selectProduct}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput 
                      placeholder={t.selectProduct} 
                      onValueChange={(value) => {
                        setProductSearch(value);
                        searchProducts(value);
                      }}
                    />
                    <CommandList>
                      {productSearchLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : products.length === 0 ? (
                        <CommandEmpty>{t.noProductFound}</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {products.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={product.website}
                              onSelect={() => handleProductSelect(product)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  orderForm.product_id === product.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{product.website}</span>
                                <span className="text-xs text-muted-foreground">
                                  DR: {product.dr} | PVP: €{product.pvp} | PC: €{product.pc}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* 4. Email Web */}
            <div>
              <Label>{t.webEmail}</Label>
              <Input 
                type="email" 
                value={orderForm.web_email} 
                onChange={(e) => setOrderForm({...orderForm, web_email: e.target.value})}
                placeholder="Auto-rellenado"
                data-testid="web-email-input"
              />
            </div>
            
            {/* 5. Tipo de Precio */}
            <div>
              <Label>{t.priceType}</Label>
              <Select value={orderForm.price_type} onValueChange={handlePriceTypeChange}>
                <SelectTrigger data-testid="price-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">{t.normal}</SelectItem>
                  <SelectItem value="sensible">{t.sensible}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* 6. Precio Compra (PC) */}
            <div>
              <Label>{t.purchasePriceLabel} ({t.pc}) €</Label>
              <Input 
                type="number" 
                value={orderForm.pc === 0 ? '' : orderForm.pc} 
                onChange={(e) => setOrderForm({...orderForm, pc: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                onFocus={(e) => { if (orderForm.pc === 0) setOrderForm({...orderForm, pc: ''}); }}
                onBlur={(e) => { if (e.target.value === '') setOrderForm({...orderForm, pc: 0}); }}
                className="bg-red-50"
                data-testid="pc-input"
              />
            </div>
            
            {/* 6b. Precio Compra Sensible (PC Sensible) */}
            <div>
              <Label>{tx('PC Sensible', 'Sensitive PC')} €</Label>
              <Input 
                type="number" 
                value={orderForm.pc_sensitive === 0 ? '' : orderForm.pc_sensitive} 
                onChange={(e) => setOrderForm({...orderForm, pc_sensitive: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                onFocus={(e) => { if (orderForm.pc_sensitive === 0) setOrderForm({...orderForm, pc_sensitive: ''}); }}
                onBlur={(e) => { if (e.target.value === '') setOrderForm({...orderForm, pc_sensitive: 0}); }}
                className="bg-orange-50"
                data-testid="pc-sensitive-input"
              />
            </div>
            
            {/* 7. Precio Venta (PVP) */}
            <div>
              <Label>{t.salePriceLabel} ({t.pvp}) €</Label>
              <Input 
                type="number" 
                value={orderForm.pvp === 0 ? '' : orderForm.pvp} 
                onChange={(e) => setOrderForm({...orderForm, pvp: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                onFocus={(e) => { if (orderForm.pvp === 0) setOrderForm({...orderForm, pvp: ''}); }}
                onBlur={(e) => { if (e.target.value === '') setOrderForm({...orderForm, pvp: 0}); }}
                className="bg-green-50"
                data-testid="pvp-input"
              />
            </div>
            
            {/* 8. Estado Pedido - DESTACADO */}
            <div className="col-span-2 bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border-2 border-purple-300">
              <Label className="text-lg font-bold text-purple-700 flex items-center gap-2 mb-2">
                <span className="bg-purple-600 text-white px-2 py-1 rounded text-sm">⚡ {t.status}</span>
              </Label>
              <Select 
                value={orderForm.status} 
                onValueChange={(v) => {
                  // Si se selecciona "Medio", abrir el diálogo obligatorio de documento
                  if (v === 'Medio' && editingOrder) {
                    setMedioDocOrder(editingOrder);
                    setMedioDocUrl('');
                    setMedioDocFiles([]);
                    setShowMedioDocDialog(true);
                    setOrderDialogOpen(false); // Cerrar el modal de edición
                    return;
                  }
                  setOrderForm({...orderForm, status: v});
                }}
              >
                <SelectTrigger className="bg-white border-2 border-purple-400 text-lg font-semibold h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {/* URL Publicada - visible y obligatoria cuando estado es Publicada */}
            <div className={orderForm.status === 'Publicada' ? 'col-span-2' : ''}>
              <Label className={orderForm.status === 'Publicada' && !orderForm.published_url ? 'text-red-600' : ''}>
                {tx('URL Publicada', 'Published URL')} {orderForm.status === 'Publicada' && <span className="text-red-600">*</span>}
              </Label>
              <Input 
                value={orderForm.published_url} 
                onChange={(e) => setOrderForm({...orderForm, published_url: e.target.value})}
                placeholder="https://..."
                className={cn(
                  orderForm.status === 'Publicada' && !orderForm.published_url && "border-red-500",
                  orderForm.status === 'Publicada' && "bg-green-50"
                )}
              />
              {orderForm.status === 'Publicada' && !orderForm.published_url && (
                <p className="text-xs text-red-500 mt-1">
                  {tx('Obligatorio cuando el estado es Publicada', 'Required when status is Published')}
                </p>
              )}
            </div>

            {/* URL de Revisión - visible cuando estado es Revision */}
            {orderForm.status === 'Revisión' && (
              <div className="col-span-2">
                <Label className="flex items-center gap-2 text-blue-700">
                  <ExternalLink className="h-4 w-4" />
                  {tx('Enlace para Revisión del Cliente *', 'Client Review Link *')}
                </Label>
                <Input 
                  data-testid="revision-url-input"
                  value={orderForm.revision_url} 
                  onChange={(e) => setOrderForm({...orderForm, revision_url: e.target.value})}
                  placeholder={tx('https://docs.google.com/document/... o enlace al contenido', 'https://docs.google.com/document/... or link to content')}
                  className="bg-blue-50 border-blue-200 border-2 border-blue-400"
                />
                <p className="text-xs text-blue-600 mt-1">
                  {tx(
                    '⚠️ OBLIGATORIO: Este enlace se enviará al cliente por email para que revise el contenido antes de publicar.',
                    '⚠️ REQUIRED: This link will be sent to the client via email to review the content before publishing.'
                  )}
                </p>
              </div>
            )}
            
            {/* 9. Redactor */}
            <div>
              <Label className={orderForm.status === 'Redactor' && !orderForm.writer_id ? 'text-red-600' : ''}>
                {t.writer} {orderForm.status === 'Redactor' && <span className="text-red-600">*</span>}
              </Label>
              <Popover open={writerOpen} onOpenChange={setWriterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={writerOpen}
                    className={cn(
                      "w-full justify-between font-normal",
                      orderForm.status === 'Redactor' && !orderForm.writer_id && "border-red-500"
                    )}
                    data-testid="writer-combobox"
                  >
                    {orderForm.writer || t.selectWriter}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t.selectWriter} />
                    <CommandList>
                      <CommandEmpty>{t.noWriterFound}</CommandEmpty>
                      <CommandGroup>
                        {writers.filter(w => w.active).map((writer) => (
                          <CommandItem
                            key={writer.id}
                            value={writer.name}
                            onSelect={() => handleWriterSelect(writer)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                orderForm.writer_id === writer.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{writer.name}</span>
                              <span className="text-xs text-muted-foreground">{writer.email}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* 10. Nº Factura Ingreso */}
            <div>
              <Label>{t.invoiceIncome}</Label>
              <Input 
                value={orderForm.invoice_income_number} 
                onChange={(e) => setOrderForm({...orderForm, invoice_income_number: e.target.value})}
                placeholder="Ej: F-2024-001"
                data-testid="invoice-income-input"
              />
            </div>
            
            {/* 11. Nº Factura Gasto con botón para abrir formulario */}
            <div className="col-span-2">
              <Label>{t.invoiceExpense}</Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  value={orderForm.invoice_expense_number} 
                  onChange={(e) => setOrderForm({...orderForm, invoice_expense_number: e.target.value})}
                  placeholder="Ej: G-2024-001"
                  data-testid="invoice-expense-input"
                  className="flex-1"
                />
                {editingOrder?.expense_id ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => viewOrderExpense(editingOrder.id)}
                    className="whitespace-nowrap text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <FileCheck className="h-4 w-4 mr-1" />
                    {tx('Modificar Factura Gasto', 'Modify Expense Invoice')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Pre-fill expense form with order data
                      setExpenseForm({
                        invoice_number: orderForm.invoice_expense_number || '',
                        order_id: editingOrder?.id || '',
                        order_number: editingOrder?.order_number || orderForm.order_number || 0,
                        order_pvp: orderForm.pvp || 0,
                        order_pc: orderForm.pc || 0,
                        invoice_date: '',
                        provider: '',
                        base_amount: orderForm.pc || 0,
                        iva: 0,
                        irpf: 0,
                        total: orderForm.pc || 0,
                        payment_method: '',
                        status: 'No pagada',
                        notes: '',
                      });
                      setExpenseDialogOpen(true);
                    }}
                    className="whitespace-nowrap"
                  >
                    <Receipt className="h-4 w-4 mr-1" />
                    {tx('Crear Factura Gasto', 'Create Expense Invoice')}
                  </Button>
                )}
              </div>
              {editingOrder?.expense_id && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1 cursor-pointer hover:underline"
                   onClick={() => viewOrderExpense(editingOrder.id)}>
                  <FileCheck className="h-3 w-3" />
                  {tx('Ver factura de gasto vinculada', 'View linked expense invoice')}
                </p>
              )}
            </div>
            
            {/* 12. Estado Pago */}
            <div>
              <Label>{t.paymentStatus}</Label>
              <Select value={orderForm.payment_status} onValueChange={(v) => setOrderForm({...orderForm, payment_status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {/* Información adicional */}
            <div className="col-span-2">
              <Label>{t.information}</Label>
              <Textarea value={orderForm.information} onChange={(e) => setOrderForm({...orderForm, information: e.target.value})} rows={2} />
            </div>

            {/* Sección Redactor - URLs y Anchor Texts */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                <Send className="h-4 w-4" />
                {tx('Datos para el Redactor', 'Writer Data')}
              </h4>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label>URL 1</Label>
                  <Input 
                    value={orderForm.url_1} 
                    onChange={(e) => setOrderForm({...orderForm, url_1: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Anchor Text 1</Label>
                  <Input 
                    value={orderForm.anchor_text_1} 
                    onChange={(e) => setOrderForm({...orderForm, anchor_text_1: e.target.value})}
                    placeholder={tx('Texto ancla', 'Anchor text')}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label>URL 2</Label>
                  <Input 
                    value={orderForm.url_2} 
                    onChange={(e) => setOrderForm({...orderForm, url_2: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Anchor Text 2</Label>
                  <Input 
                    value={orderForm.anchor_text_2} 
                    onChange={(e) => setOrderForm({...orderForm, anchor_text_2: e.target.value})}
                    placeholder={tx('Texto ancla', 'Anchor text')}
                  />
                </div>
              </div>
              
              {/* Enlaces Adicionales del Cliente */}
              <div className="col-span-2 space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  🔗 {tx('Enlaces Adicionales (solicitados por cliente)', 'Additional Links (requested by client)')}
                </h4>
                
                {/* Enlace Interno */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="add_internal_link"
                      checked={orderForm.add_internal_link}
                      onChange={(e) => setOrderForm({...orderForm, add_internal_link: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="add_internal_link" className="text-blue-700 font-medium">
                      {tx('Enlace Interno', 'Internal Link')}
                    </Label>
                  </div>
                  {orderForm.add_internal_link && (
                    <div className="grid grid-cols-2 gap-2 ml-6">
                      <Input
                        value={orderForm.internal_link_url}
                        onChange={(e) => setOrderForm({...orderForm, internal_link_url: e.target.value})}
                        placeholder="URL del enlace interno"
                        className="bg-white"
                      />
                      <Input
                        value={orderForm.internal_link_anchor}
                        onChange={(e) => setOrderForm({...orderForm, internal_link_anchor: e.target.value})}
                        placeholder="Anchor del enlace interno"
                        className="bg-white"
                      />
                    </div>
                  )}
                </div>
                
                {/* Enlace de Autoridad */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="add_authority_link"
                      checked={orderForm.add_authority_link}
                      onChange={(e) => setOrderForm({...orderForm, add_authority_link: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="add_authority_link" className="text-amber-700 font-medium">
                      {tx('Enlace de Autoridad', 'Authority Link')}
                    </Label>
                  </div>
                  {orderForm.add_authority_link && (
                    <div className="grid grid-cols-2 gap-2 ml-6">
                      <Input
                        value={orderForm.authority_link_url}
                        onChange={(e) => setOrderForm({...orderForm, authority_link_url: e.target.value})}
                        placeholder="URL del enlace de autoridad"
                        className="bg-white"
                      />
                      <Input
                        value={orderForm.authority_link_anchor}
                        onChange={(e) => setOrderForm({...orderForm, authority_link_anchor: e.target.value})}
                        placeholder="Anchor del enlace de autoridad"
                        className="bg-white"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <Label>{tx('Instrucciones para el Redactor', 'Writer Instructions')}</Label>
                <Textarea 
                  value={orderForm.writer_instructions} 
                  onChange={(e) => setOrderForm({...orderForm, writer_instructions: e.target.value})}
                  rows={3}
                  placeholder={tx('Instrucciones específicas para el redactor...', 'Specific instructions for the writer...')}
                />
              </div>
              
              {/* Modificaciones Solicitadas por el Cliente - solo lectura */}
              {editingOrder && editingOrder.modification_notes && (
                <div className="col-span-2">
                  <Label className="flex items-center gap-2 text-purple-700">
                    <MessageSquare className="h-4 w-4" />
                    {tx('Modificaciones Solicitadas por el Cliente', 'Modifications Requested by Client')}
                  </Label>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-md mt-1">
                    <p className="text-sm text-purple-900 whitespace-pre-wrap">{editingOrder.modification_notes}</p>
                    {editingOrder.modification_requested_at && (
                      <p className="text-xs text-purple-500 mt-2">
                        {tx('Solicitado el', 'Requested on')}: {new Date(editingOrder.modification_requested_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  {editingOrder.previous_status_before_modification && (
                    <p className="text-xs text-purple-600 mt-1">
                      {tx(`Estado anterior: ${editingOrder.previous_status_before_modification}`, `Previous status: ${editingOrder.previous_status_before_modification}`)}
                    </p>
                  )}
                </div>
              )}
              
              {/* Campo de Notas Internas (solo admin) */}
              <div className="col-span-2">
                <Label className="flex items-center gap-2 text-orange-700">
                  <FileText className="h-4 w-4" />
                  {tx('Notas Internas (solo admins)', 'Internal Notes (admin only)')}
                </Label>
                <Textarea 
                  value={orderForm.internal_notes} 
                  onChange={(e) => setOrderForm({...orderForm, internal_notes: e.target.value})}
                  rows={2}
                  className="bg-orange-50 border-orange-200"
                  placeholder={tx('Notas internas que nunca se enviarán al cliente...', 'Internal notes that will never be sent to the client...')}
                />
                <p className="text-xs text-orange-600 mt-1">
                  {tx('Estas notas son privadas y no se comparten con el cliente.', 'These notes are private and not shared with the client.')}
                </p>
              </div>

              {/* Campo de Motivo de Incidencia - solo visible si estado es Incidencia */}
              {orderForm.status === 'Incidencia' && (
                <div className="col-span-2">
                  <Label className="flex items-center gap-2 text-red-700">
                    <X className="h-4 w-4" />
                    {tx('Motivo de la Incidencia *', 'Incident Reason *')}
                  </Label>
                  <Textarea 
                    value={orderForm.incident_reason} 
                    onChange={(e) => setOrderForm({...orderForm, incident_reason: e.target.value})}
                    rows={2}
                    className="bg-red-50 border-red-300"
                    placeholder={tx('Describe el motivo de la incidencia (se enviará al cliente por email)...', 'Describe the incident reason (will be sent to client via email)...')}
                  />
                  <p className="text-xs text-red-600 mt-1">
                    {tx('Este mensaje se enviará por email al cliente al guardar.', 'This message will be sent to the client via email when saved.')}
                  </p>
                </div>
              )}

              {/* Indicador de envío al redactor - solo informativo */}
              {editingOrder && editingOrder.sent_to_writer && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    {tx('Enviado al redactor', 'Sent to writer')}
                  </span>
                </div>
              )}
            </div>
            
            {/* 13. Archivos del Cliente (desde checkout) */}
            {editingOrder && (editingOrder.article_file || editingOrder.image_file_1 || editingOrder.image_file_2) && (
              <div className="col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-blue-800 font-semibold flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4" />
                  {tx('Archivos del Cliente', 'Client Files')}
                </Label>
                <div className="space-y-2">
                  {editingOrder.article_file && (
                    <div className="flex items-center justify-between p-2 bg-white rounded-md">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">Artículo:</span>
                        <span className="text-sm">{editingOrder.article_file_original || editingOrder.article_file}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}/orders/${editingOrder.id}/files/${editingOrder.article_file}`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" /> Descargar
                      </Button>
                    </div>
                  )}
                  {editingOrder.image_file_1 && (
                    <div className="flex items-center justify-between p-2 bg-white rounded-md">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">Imagen 1:</span>
                        <span className="text-sm">{editingOrder.image_file_1_original || editingOrder.image_file_1}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}/orders/${editingOrder.id}/files/${editingOrder.image_file_1}`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" /> Descargar
                      </Button>
                    </div>
                  )}
                  {editingOrder.image_file_2 && (
                    <div className="flex items-center justify-between p-2 bg-white rounded-md">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">Imagen 2:</span>
                        <span className="text-sm">{editingOrder.image_file_2_original || editingOrder.image_file_2}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}/orders/${editingOrder.id}/files/${editingOrder.image_file_2}`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" /> Descargar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* 14. Archivos adjuntos - Siempre visible */}
            <div className="col-span-2">
              <Label>{tx('Archivos Adjuntos', 'Attached Files')}</Label>
              <div className="mt-2 space-y-2">
                {/* Lista de archivos ya subidos (solo cuando se edita) */}
                {editingOrder && orderFiles.length > 0 && (
                  <div className="space-y-1">
                    {orderFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm">{file.original_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadFile(editingOrder.id, file.filename, file.original_name)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFile(editingOrder.id, file.filename)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Lista de archivos pendientes (cuando se crea nuevo pedido) */}
                {!editingOrder && pendingFiles.length > 0 && (
                  <div className="space-y-1">
                    {pendingFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm">{file.original_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          <Badge variant="outline" className="text-yellow-600 border-yellow-400">
                            {tx('Pendiente', 'Pending')}
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePendingFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Botón para subir archivo */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".doc,.docx,.pdf,.txt,.xls,.xlsx"
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    data-testid="upload-file-btn"
                  >
                    {uploadingFile ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {tx('Subir Archivo', 'Upload File')}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {tx('.doc, .docx, .pdf, .txt, .xls, .xlsx', '.doc, .docx, .pdf, .txt, .xls, .xlsx')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveOrder} data-testid="save-order-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Dialog */}
      <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCustomer ? t.editCustomer : t.newCustomer}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.name} *</Label>
                <Input value={customerForm.name} onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})} />
              </div>
              <div>
                <Label>{t.company}</Label>
                <Input value={customerForm.company} onChange={(e) => setCustomerForm({...customerForm, company: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.email} *</Label>
                <Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})} />
              </div>
              <div>
                <Label>{t.phone}</Label>
                <Input value={customerForm.phone} onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CIF/NIF</Label>
                <Input 
                  value={customerForm.nif} 
                  onChange={(e) => setCustomerForm({...customerForm, nif: e.target.value})} 
                  placeholder="B12345678"
                />
              </div>
              <div>
                <Label>{tx('Dirección de Facturación', 'Billing Address')}</Label>
                <Input 
                  value={customerForm.billing_address} 
                  onChange={(e) => setCustomerForm({...customerForm, billing_address: e.target.value})} 
                  placeholder={tx('Calle, Ciudad, CP', 'Street, City, ZIP')}
                />
              </div>
            </div>
            <div>
              <Label>{t.notes}</Label>
              <Textarea value={customerForm.notes} onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveCustomer}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Writer Dialog */}
      <Dialog open={writerDialogOpen} onOpenChange={setWriterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingWriter ? t.editWriter : t.newWriter}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>{t.name}</Label>
              <Input value={writerForm.name} onChange={(e) => setWriterForm({...writerForm, name: e.target.value})} />
            </div>
            <div>
              <Label>{t.email}</Label>
              <Input type="email" value={writerForm.email} onChange={(e) => setWriterForm({...writerForm, email: e.target.value})} />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="writer-active"
                checked={writerForm.active}
                onChange={(e) => setWriterForm({...writerForm, active: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="writer-active">{t.active}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriterDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveWriter}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Download Dialog */}
      <Dialog open={showExpenseDownloadDialog} onOpenChange={setShowExpenseDownloadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tx('Descargar Gastos', 'Download Expenses')}</DialogTitle>
            <DialogDescription>
              {tx('Selecciona el rango de fechas', 'Select date range')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={expenseDownloadPreset === 'last_month' ? 'default' : 'outline'}
                onClick={() => {
                  setExpenseDownloadPreset('last_month');
                  setExpenseDownloadDateFrom('');
                  setExpenseDownloadDateTo('');
                }}
              >
                {tx('Mes Pasado', 'Last Month')}
              </Button>
              <Button
                variant={expenseDownloadPreset === '' ? 'default' : 'outline'}
                onClick={() => setExpenseDownloadPreset('')}
              >
                {tx('Personalizado', 'Custom')}
              </Button>
            </div>
            
            {expenseDownloadPreset === '' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{tx('Desde', 'From')}</Label>
                  <Input
                    type="date"
                    value={expenseDownloadDateFrom}
                    onChange={(e) => setExpenseDownloadDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tx('Hasta', 'To')}</Label>
                  <Input
                    type="date"
                    value={expenseDownloadDateTo}
                    onChange={(e) => setExpenseDownloadDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseDownloadDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleDownloadExpenses}>
              <Download className="h-4 w-4 mr-2" />
              {tx('Descargar', 'Download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Income Dialog */}
      <Dialog open={showIncomeDialog} onOpenChange={setShowIncomeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingIncome 
                ? (tx('Editar Factura de Ingreso', 'Edit Income Invoice'))
                : (tx('Nueva Factura de Ingreso', 'New Income Invoice'))
              }
              {editingIncome?.source_type === 'recharge' && (
                <Badge className="ml-2 bg-blue-100 text-blue-700">Recarga de Monedero</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.invoiceNumber} *</Label>
                <Input
                  value={incomeForm.invoice_number}
                  onChange={(e) => setIncomeForm({...incomeForm, invoice_number: e.target.value})}
                  placeholder="INV-2024-001"
                  readOnly={editingIncome?.source_type === 'recharge'}
                  className={editingIncome?.source_type === 'recharge' ? 'bg-muted' : ''}
                />
              </div>
              {/* Only show Order Number for manual invoices */}
              {editingIncome?.source_type !== 'recharge' && (
                <div>
                  <Label>{tx('Nº Pedido', 'Order Number')}</Label>
                  <Input
                    type="number"
                    value={incomeForm.order_number}
                    onChange={(e) => setIncomeForm({...incomeForm, order_number: parseInt(e.target.value) || 0})}
                  />
                </div>
              )}
            </div>

            {/* Customer selector - Only for manual invoices */}
            {editingIncome?.source_type !== 'recharge' && (
              <div>
                <Label>{tx('Seleccionar Cliente Existente', 'Select Existing Client')}</Label>
                <Select onValueChange={handleIncomeCustomerSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder={tx('Seleccionar cliente...', 'Select client...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.length === 0 ? (
                      <SelectItem value="none" disabled>
                        {tx('No hay clientes', 'No clients')}
                      </SelectItem>
                    ) : (
                      customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.company || customer.name} {customer.nif ? `(${customer.nif})` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{tx('Cliente', 'Client')} *</Label>
                <Input
                  value={incomeForm.client_name}
                  onChange={(e) => setIncomeForm({...incomeForm, client_name: e.target.value})}
                  readOnly={editingIncome?.source_type === 'recharge'}
                  className={editingIncome?.source_type === 'recharge' ? 'bg-muted' : ''}
                />
              </div>
              <div>
                <Label>{tx('NIF/CIF', 'Tax ID')}</Label>
                <Input
                  value={incomeForm.client_nif}
                  onChange={(e) => setIncomeForm({...incomeForm, client_nif: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>{tx('Descripción', 'Description')}</Label>
              <Textarea
                value={incomeForm.description}
                onChange={(e) => setIncomeForm({...incomeForm, description: e.target.value})}
                rows={2}
                readOnly={editingIncome?.source_type === 'recharge'}
                className={editingIncome?.source_type === 'recharge' ? 'bg-muted' : ''}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t.baseAmount} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.base_amount}
                  onChange={(e) => setIncomeForm({...incomeForm, base_amount: parseFloat(e.target.value) || 0})}
                  readOnly={editingIncome?.source_type === 'recharge'}
                  className={editingIncome?.source_type === 'recharge' ? 'bg-muted' : ''}
                />
              </div>
              <div>
                <Label>{t.iva} (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.iva}
                  onChange={(e) => setIncomeForm({...incomeForm, iva: parseFloat(e.target.value) || 0})}
                  readOnly={editingIncome?.source_type === 'recharge'}
                  className={editingIncome?.source_type === 'recharge' ? 'bg-muted' : ''}
                />
              </div>
              <div>
                <Label>{t.irpf} (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.irpf}
                  onChange={(e) => setIncomeForm({...incomeForm, irpf: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.total}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.total}
                  readOnly
                  className="bg-green-50 font-bold text-green-700"
                />
              </div>
              <div>
                <Label>{tx('Fecha Factura', 'Invoice Date')}</Label>
                <Input
                  type="date"
                  value={incomeForm.invoice_date}
                  onChange={(e) => setIncomeForm({...incomeForm, invoice_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Payment Method - Only for manual invoices */}
              {editingIncome?.source_type !== 'recharge' && (
                <div>
                  <Label>{t.paymentMethod}</Label>
                  <Select
                    value={incomeForm.payment_method}
                    onValueChange={(value) => setIncomeForm({...incomeForm, payment_method: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={tx('Seleccionar método', 'Select method')} />
                    </SelectTrigger>
                    <SelectContent>
                      {INCOME_PAYMENT_METHODS.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>{tx('Estado', 'Status')}</Label>
                {editingIncome?.source_type === 'recharge' ? (
                  <Input
                    value="Pagada"
                    readOnly
                    className="bg-green-50 text-green-700 font-medium"
                  />
                ) : (
                  <Select
                    value={incomeForm.status}
                    onValueChange={(value) => setIncomeForm({...incomeForm, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INCOME_STATUSES.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div>
              <Label>{t.notes}</Label>
              <Textarea
                value={incomeForm.notes}
                onChange={(e) => setIncomeForm({...incomeForm, notes: e.target.value})}
                rows={3}
              />
            </div>

            {/* File Upload Section - Only for manual invoices */}
            {editingIncome?.source_type !== 'recharge' && (
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4" />
                  {tx('Archivos Adjuntos', 'Attached Files')}
                </Label>
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={incomeFileInputRef}
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleIncomeFileSelect}
                  multiple
                />
                
                {/* Upload button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => incomeFileInputRef.current?.click()}
                  className="mb-3"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {tx('Subir Factura PDF', 'Upload Invoice PDF')}
                </Button>

                {/* Existing files (when editing) */}
                {incomeFiles.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm text-muted-foreground mb-2">
                      {tx('Archivos existentes:', 'Existing files:')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {incomeFiles.map((file) => (
                        <div key={file.filename} className="flex items-center gap-2 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">
                          <FileText className="h-4 w-4 text-green-600" />
                          <span className="text-sm">{file.original_name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleDownloadIncomeFile(editingIncome.id, file.filename, file.original_name)}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteIncomeFile(editingIncome.id, file.filename)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending files (to be uploaded) */}
              {pendingIncomeFiles.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {tx('Archivos por subir:', 'Files to upload:')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {pendingIncomeFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">{file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleRemovePendingIncomeFile(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncomeDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleSaveIncome}>
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Income Download Dialog */}
      <Dialog open={showIncomeDownloadDialog} onOpenChange={setShowIncomeDownloadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tx('Descargar Facturas de Ingresos', 'Download Income Invoices')}</DialogTitle>
            <DialogDescription>
              {tx('Selecciona el rango de fechas', 'Select date range')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={incomeDownloadPreset === 'last_month' ? 'default' : 'outline'}
                onClick={() => {
                  setIncomeDownloadPreset('last_month');
                  setIncomeDownloadDateFrom('');
                  setIncomeDownloadDateTo('');
                }}
              >
                {tx('Mes Pasado', 'Last Month')}
              </Button>
              <Button
                variant={incomeDownloadPreset === '' ? 'default' : 'outline'}
                onClick={() => setIncomeDownloadPreset('')}
              >
                {tx('Personalizado', 'Custom')}
              </Button>
            </div>
            
            {incomeDownloadPreset === '' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{tx('Desde', 'From')}</Label>
                  <Input
                    type="date"
                    value={incomeDownloadDateFrom}
                    onChange={(e) => setIncomeDownloadDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{tx('Hasta', 'To')}</Label>
                  <Input
                    type="date"
                    value={incomeDownloadDateTo}
                    onChange={(e) => setIncomeDownloadDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncomeDownloadDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleDownloadIncome}>
              <Download className="h-4 w-4 mr-2" />
              {tx('Descargar', 'Download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              {t.importSuccess}
            </DialogTitle>
            <DialogDescription>
              {t.importOrdersDesc}
            </DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-3 py-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-green-700 font-medium">{t.ordersImported}</span>
                <Badge className="bg-green-600">{importResult.created || 0}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-700 font-medium">{t.ordersUpdated}</span>
                <Badge className="bg-blue-600">{importResult.updated || 0}</Badge>
              </div>
              {importResult.skipped > 0 && (
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-yellow-700 font-medium">{t.ordersSkipped}</span>
                  <Badge className="bg-yellow-600">{importResult.skipped}</Badge>
                </div>
              )}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="text-red-700 font-medium mb-2">{tx('Errores:', 'Errors:')}</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {importResult.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {importResult.errors.length > 5 && (
                      <li>... {tx('y', 'and')} {importResult.errors.length - 5} {tx('más', 'more')}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowImportDialog(false)}>
              {tx('Cerrar', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medio Template Popup */}
      <Dialog open={showMedioPopup} onOpenChange={setShowMedioPopup}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {tx('Plantilla de Email para Medio', 'Email Template for Media')}
            </DialogTitle>
            <DialogDescription>
              {tx(
                'Copia esta plantilla para enviar al medio. Selecciona el idioma según el país del medio.',
                'Copy this template to send to the media. Select the language according to the media country.'
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Order Info */}
            {medioPopupOrder && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p><strong>{tx('Pedido', 'Order')}:</strong> #{medioPopupOrder.order_number}</p>
                <p><strong>{tx('Medio', 'Media')}:</strong> {medioPopupOrder.product_url}</p>
                <p><strong>{tx('Email del medio', 'Media email')}:</strong> {medioPopupOrder.product_owner_email || 'No disponible'}</p>
                <p><strong>{tx('Precio', 'Price')}:</strong> €{medioPopupOrder.product_pvp || medioPopupOrder.pvp || 0}</p>
              </div>
            )}
            
            {/* Language Selector */}
            <div className="flex items-center gap-2">
              <Label>{tx('Idioma de la plantilla', 'Template language')}:</Label>
              <Select value={medioPopupLanguage} onValueChange={setMedioPopupLanguage}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">🇪🇸 Español</SelectItem>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                  <SelectItem value="fr">🇫🇷 Français</SelectItem>
                  <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                  <SelectItem value="it">🇮🇹 Italiano</SelectItem>
                  <SelectItem value="pt">🇵🇹 Português</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Template Preview */}
            <div className="relative">
              <Label className="mb-2 block">{tx('Plantilla', 'Template')}:</Label>
              <div className="p-4 bg-gray-50 rounded-lg border font-mono text-sm whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                {generateMedioTemplate(medioPopupOrder, medioPopupLanguage)}
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowMedioPopup(false)}>
              {tx('Cerrar', 'Close')}
            </Button>
            <Button 
              onClick={handleCopyMedioTemplate}
              className={medioCopied ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {medioCopied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {tx('¡Copiado!', 'Copied!')}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  {tx('Copiar plantilla', 'Copy template')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medio Document Requirement Dialog */}
      <Dialog open={showMedioDocDialog} onOpenChange={(open) => {
        setShowMedioDocDialog(open);
        if (!open) {
          setMedioDocUrl('');
          setMedioDocFiles([]);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-purple-600" />
              {tx('Documentos para el Medio', 'Documents for Media')}
            </DialogTitle>
            <DialogDescription>
              {tx(
                'Adjunta los documentos e imágenes para enviar al medio. Puedes combinar enlace de Drive con archivos subidos.',
                'Attach documents and images to send to media. You can combine Drive link with uploaded files.'
              )}
            </DialogDescription>
          </DialogHeader>
          
          {medioDocOrder && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  <strong>{tx('Pedido', 'Order')}:</strong> #{medioDocOrder.order_number}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>{tx('Medio', 'Media')}:</strong> {medioDocOrder.product_url}
                </p>
              </div>

              {/* Customer's pre-attached files (from checkout) — auto-included in send */}
              {(() => {
                const customerAttachments = [];
                if (medioDocOrder.article_file) {
                  customerAttachments.push({
                    name: medioDocOrder.article_file_original || medioDocOrder.article_file,
                    file: medioDocOrder.article_file,
                    label: tx('Artículo', 'Article'),
                  });
                }
                if (medioDocOrder.image_file_1) {
                  customerAttachments.push({
                    name: medioDocOrder.image_file_1_original || medioDocOrder.image_file_1,
                    file: medioDocOrder.image_file_1,
                    label: tx('Imagen 1', 'Image 1'),
                  });
                }
                if (medioDocOrder.image_file_2) {
                  customerAttachments.push({
                    name: medioDocOrder.image_file_2_original || medioDocOrder.image_file_2,
                    file: medioDocOrder.image_file_2,
                    label: tx('Imagen 2', 'Image 2'),
                  });
                }
                if (customerAttachments.length === 0) return null;
                return (
                  <div className="space-y-2 p-3 border-2 border-amber-200 bg-amber-50/50 rounded-lg" data-testid="customer-attachments-block">
                    <Label className="flex items-center gap-2 text-amber-900">
                      📎 {tx('Archivos adjuntados por el cliente', 'Files attached by the customer')}
                    </Label>
                    <p className="text-xs text-amber-800 mb-1">
                      {tx(
                        'Estos archivos se incluirán automáticamente al enviar al medio (no hace falta volver a subirlos).',
                        'These files will be included automatically when sending to media (no need to re-upload).'
                      )}
                    </p>
                    {customerAttachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs bg-white border border-amber-200 p-2 rounded">
                        <span className="flex items-center gap-2 text-amber-900">
                          <Check className="h-3 w-3 text-green-600" />
                          <span className="font-medium">{att.label}:</span>
                          <span className="truncate max-w-xs" title={att.name}>{att.name}</span>
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-amber-700 hover:text-amber-900"
                          onClick={() => window.open(`${API}/orders/${medioDocOrder.id}/files/${att.file}`, '_blank')}
                          data-testid={`view-customer-file-${idx}`}
                        >
                          {tx('Ver', 'View')}
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Drive URL Section */}
              <div className="space-y-2 p-3 border rounded-lg">
                <Label htmlFor="medio-doc-url" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-600" />
                  {tx('Enlace de Google Drive o URL (opcional)', 'Google Drive Link or URL (optional)')}
                </Label>
                <Input
                  id="medio-doc-url"
                  value={medioDocUrl}
                  onChange={(e) => setMedioDocUrl(e.target.value)}
                  placeholder="https://docs.google.com/document/d/..."
                />
              </div>
              
              {/* File Upload Section */}
              <div className="space-y-2 p-3 border rounded-lg">
                <Label htmlFor="medio-doc-files" className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-green-600" />
                  {tx('Subir Archivos (múltiples permitidos)', 'Upload Files (multiple allowed)')}
                </Label>
                <Input
                  id="medio-doc-files"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.webp"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      setMedioDocFiles(prev => [...prev, ...files]);
                      // Reset input to allow selecting same files again
                      e.target.value = '';
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {tx(
                    'Formatos: PDF, Word, Excel, Imágenes (JPG, PNG). Puedes seleccionar varios archivos.',
                    'Formats: PDF, Word, Excel, Images (JPG, PNG). You can select multiple files.'
                  )}
                </p>
                
                {/* Show selected files */}
                {medioDocFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      {tx('Archivos seleccionados:', 'Selected files:')}
                    </p>
                    {medioDocFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between text-xs bg-green-50 p-2 rounded">
                        <span className="flex items-center gap-1 text-green-700">
                          <Check className="h-3 w-3" />
                          {file.name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                          onClick={() => setMedioDocFiles(prev => prev.filter((_, i) => i !== index))}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 text-xs"
                      onClick={() => document.getElementById('medio-doc-files')?.click()}
                    >
                      + {tx('Añadir más archivos', 'Add more files')}
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Summary */}
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>{tx('Resumen:', 'Summary:')}</strong>{' '}
                  {medioDocUrl.trim() ? '1 enlace' : '0 enlaces'} + {medioDocFiles.length} archivo{medioDocFiles.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowMedioDocDialog(false)}>
              {tx('Cancelar', 'Cancel')}
            </Button>
            <Button 
              onClick={handleMedioStatusWithDocument}
              disabled={uploadingMedioDoc || (
                !medioDocUrl.trim() &&
                medioDocFiles.length === 0 &&
                !medioDocOrder?.article_file &&
                !medioDocOrder?.image_file_1 &&
                !medioDocOrder?.image_file_2
              )}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploadingMedioDoc ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {tx('Procesando...', 'Processing...')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {tx('Enviar a Medio', 'Send to Media')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Email preview / inline-edit dialog === */}
      <Dialog
        open={emailPreview.open}
        onOpenChange={(o) => { if (!o && !emailPreview.sending) closeEmailPreview(); }}
      >
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="email-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              {tx('Vista previa del email', 'Email preview')}
              {emailPreview.status && (
                <Badge variant="secondary" className="ml-2">{emailPreview.status}</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {tx(
                'Revisa el email que se enviará al cambiar el estado. Puedes editar el asunto y el contenido HTML antes de confirmar.',
                'Review the email that will be sent when changing status. You can edit subject and HTML content before confirming.'
              )}
            </DialogDescription>
          </DialogHeader>

          {emailPreview.loading ? (
            <div className="py-12 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              {tx('Generando vista previa…', 'Rendering preview…')}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{tx('Para', 'To')}</Label>
                  <Input
                    value={emailPreview.toEmail}
                    onChange={(e) => setEmailPreview(p => ({ ...p, toEmail: e.target.value }))}
                    className="text-sm"
                    data-testid="email-preview-to"
                  />
                </div>
                <div>
                  <Label className="text-xs">{tx('Responder a', 'Reply to')}</Label>
                  <Input
                    value={emailPreview.replyTo}
                    onChange={(e) => setEmailPreview(p => ({ ...p, replyTo: e.target.value }))}
                    className="text-sm"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">{tx('Asunto', 'Subject')}</Label>
                <Input
                  value={emailPreview.subject}
                  onChange={(e) => setEmailPreview(p => ({ ...p, subject: e.target.value }))}
                  className="font-medium"
                  data-testid="email-preview-subject"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-xs">{tx('Contenido', 'Content')}</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setEmailPreview(p => ({ ...p, showHtmlEditor: !p.showHtmlEditor }))}
                    data-testid="email-preview-toggle-editor"
                  >
                    {emailPreview.showHtmlEditor
                      ? tx('Ver vista previa', 'Show preview')
                      : tx('Editar HTML', 'Edit HTML')
                    }
                  </Button>
                </div>
              </div>

              {emailPreview.showHtmlEditor ? (
                <Textarea
                  value={emailPreview.html}
                  onChange={(e) => setEmailPreview(p => ({ ...p, html: e.target.value }))}
                  className="min-h-[420px] font-mono text-xs"
                  data-testid="email-preview-html-textarea"
                />
              ) : (
                <iframe
                  title="email-preview"
                  srcDoc={emailPreview.html}
                  className="w-full min-h-[420px] border rounded bg-white"
                  sandbox="allow-same-origin"
                  data-testid="email-preview-iframe"
                />
              )}
            </div>
          )}

          <DialogFooter className="gap-2 pt-3 border-t">
            <Button
              variant="outline"
              onClick={closeEmailPreview}
              disabled={emailPreview.sending}
              data-testid="email-preview-cancel"
            >
              {tx('Cancelar', 'Cancel')}
            </Button>
            <Button
              onClick={confirmEmailPreviewSend}
              disabled={emailPreview.loading || emailPreview.sending || !emailPreview.subject}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="email-preview-confirm"
            >
              {emailPreview.sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {tx('Enviando…', 'Sending…')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {tx('Confirmar y enviar', 'Confirm & send')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMTab;
